import { useState, useEffect, useMemo, useRef } from "react";
import { goBack } from "@/lib/goBack";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Save, Trash2, Plus, Loader2, ArrowLeft, Ruler, DoorOpen, Package as PackageIcon,
  Copy, Sparkles, Check, X, GripVertical, DollarSign, Link2, Send, HardHat, Hammer, Eye, CheckCircle2, ShieldOff, MoreHorizontal,
  CalendarClock, ChevronDown, ChevronRight, Lock, ShieldCheck, Undo2, Briefcase,
} from "lucide-react";
import { fmtCurrencyExact, fmtCurrency, fmtDateTime, FENCE_LABELS } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import DealPeek from "@/components/DealPeek";

const FENCE_TYPE_OPTIONS = Object.entries(FENCE_LABELS).filter(([k]) => !["labour", "incidental"].includes(k));
import { COLORBOND_COLOURS, GATE_TEMPLATES, HEIGHT_OPTIONS, HAS_COLOUR_PICKER, GATE_STYLES, materialUnitToSection, calcSectionMaterials } from "@/lib/fencing";
import FabricationCalculator from "@/components/FabricationCalculator";
import { LivePreviewPanel, MobilePreviewFab } from "@/components/LivePreviewPanel";
import { QuoteAttachments } from "@/components/QuoteAttachments";
import { QuoteVisuals } from "@/components/QuoteVisuals";
// SitePlanReader UI hidden (31 Oct 2026) — import kept out; legacy helpers below remain.
import ConfirmSendQuoteDialog from "@/components/ConfirmSendQuoteDialog";
import DuplicateQuoteDialog from "@/components/DuplicateQuoteDialog";
import { fabInputSummary, FAB_TYPE_LABEL } from "@/lib/fabrication";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { MaterialEditDialog } from "@/components/MaterialEditDialog";
import { ApprovalThread } from "@/components/ApprovalThread";

const DEFAULT_GP = 40;

// Iter-66: a "Line item" is a standalone customer-facing scope block.
// Each line wraps 1+ SECTIONS (fence / gate / labour / daily / fab / extra)
// that together deliver that scope. On the customer PDF we show one navy
// tile per line with its AI narrative + total. Child sections are hidden
// by default.
const newLineItem = (title = "") => ({
  id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2),
  title,
  scope_narrative: "",
  show_sections_to_customer: false,
  sections: [],
  cost_total: 0,
  ai_meta: null, // { mode, sections_hash, generated_at }
});

const flatSections = (lines) => (lines || []).flatMap((l) => l.sections || []);

const emptyQuote = () => ({
  customer_name: "", customer_email: "", customer_phone: "", site_address: "",
  company_id: "", contact_id: "", deal_id: "",
  fence_type: "",   // top-level "primary" fence type — auto-synced from first fence section
  linear_meters: 0, square_meters: 0,
  line_items: [],
  sections: [],
  lines: [newLineItem("Fencing works")],
  version: 2,
  gp_margin_percent: DEFAULT_GP,
  scope_summary: "", status: "draft", notes: "",
});

const SECTION_LETTERS = "ABCDEFGHIJKLMNOP";

// SHA-1-ish hash of the section shape used by ai_routes to decide
// whether an AI narrative is stale after the estimator edits sections.
// Must match the server's `_sections_text` -> sha1[:12] logic loosely
// enough that any change to the section list flips the hash.
function _sectionsFingerprint(sections) {
  const parts = (sections || []).map((s) => [
    s.section_type || "", s.label || "", s.unit || "",
    s.qty || 0, s.length_m || 0, s.height_mm || 0,
    s.colour || "", s.fence_type || "", s.gate_style || "",
    s.notes || "",
  ].join("|"));
  return parts.join("¦");
}

export default function QuoteBuilderV2() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [quoteId, setQuoteId] = useState(id || null);
  const [quote, setQuote] = useState(() => {
    const q = emptyQuote();
    if (!id) {
      const sp = new URLSearchParams(window.location.search);
      for (const k of ["customer_name", "customer_email", "customer_phone", "site_address", "company_id"]) {
        if (sp.get(k)) q[k] = sp.get(k);
      }
      if (sp.get("deal")) q.deal_id = sp.get("deal");
    }
    return q;
  });
  const [addOpen, setAddOpen] = useState(null); // Iter-66: null | { lineIdx, type }
  const [editingMaterialId, setEditingMaterialId] = useState(null); // material master live-edit from a section
  const [fabDialog, setFabDialog] = useState({ open: false, lineIdx: null, editIndex: null });
  const [fabDraft, setFabDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoState, setAutoState] = useState(null); // null | "saving" | "Draft saved …"
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const dirtyRef = useRef(false);
  const applyingRef = useRef(false);
  const loadedRef = useRef(false);
  const savingRef = useRef(false);
  const quoteRef = useRef(quote);
  quoteRef.current = quote;

  // ---- Approval workflow (Trent, Jun 2026) ----
  const { user } = useAuth();
  const approval = quote.approval_status || ((quote.status === "sent" || quote.status === "accepted") ? "approved" : "draft");
  // changes_requested is EDITABLE — approver push-back unlocks the quote (Trent, Oct 2026)
  const locked = !!quoteId && approval !== "draft" && approval !== "changes_requested";
  const isApprover = !!(user?.quote_approver || user?.role === "super_admin");
  const revision = quote.revision || 1;
  // Iter-87: latest submit/resubmit trail event — surfaces the submitter's message while pending.
  const pendingSubmitEv = useMemo(() => {
    if (approval !== "pending_approval") return null;
    return [...(quote.approval_trail || [])].reverse().find((t) => t.event === "submitted" || t.event === "resubmitted") || null;
  }, [quote.approval_trail, approval]);

  // Pricing DB is retired — Materials is the single source of truth. All
  // section pickers read from `materials` below; composites (formerly
  // "Pricing entries") float to the top via the is_composite flag.
  const { data: companies = [] } = useQuery({
    queryKey: ["companies-all-q2"],
    queryFn: async () => (await api.get("/companies")).data,
  });
  const { data: materials = [] } = useQuery({
    queryKey: ["materials-all-q2"],
    queryFn: async () => (await api.get("/materials")).data,
  });
  const { data: allDeals = [] } = useQuery({
    queryKey: ["deals-for-quote"],
    queryFn: async () => (await api.get("/deals")).data,
  });
  const { data: companyContacts = [] } = useQuery({
    queryKey: ["quote-contacts", quote.company_id],
    enabled: !!quote.company_id,
    queryFn: async () => (await api.get("/contacts", { params: { company_id: quote.company_id } })).data,
  });

  const applyDeal = async (dealId) => {
    try {
      const d = (await api.get(`/deals/${dealId}`)).data;
      let patch = { deal_id: dealId };
      if (d.company_id) {
        const co = companies.find((c) => c.id === d.company_id) ||
          (await api.get("/companies")).data.find((c) => c.id === d.company_id);
        const cts = (await api.get("/contacts", { params: { company_id: d.company_id } })).data;
        const pc = cts.find((c) => c.id === d.contact_id) || cts.find((c) => c.is_primary) || cts[0];
        const addr = (co?.addresses || [])[0];
        const addrStr = addr ? [addr.line1, addr.suburb, addr.state, addr.postcode].filter(Boolean).join(", ") : "";
        patch = {
          ...patch,
          company_id: d.company_id,
          contact_id: pc?.id || "",
          customer_name: pc ? `${pc.first_name || ""} ${pc.last_name || ""}`.trim() : (co?.name || d.title),
          customer_email: pc?.email || "",
          customer_phone: pc?.phone || pc?.mobile_phone || "",
          site_address: addrStr || quoteRef.current.site_address || "",
        };
      } else {
        patch.customer_name = quoteRef.current.customer_name || d.title || "";
      }
      setQuote((q) => ({ ...q, ...patch }));
      toast.success(`Linked to ${d.job_number || d.title} — client details filled`);
    } catch {
      toast.error("Could not load that deal");
    }
  };

  const dealAppliedRef = useRef(false);
  useEffect(() => {
    if (!id && !dealAppliedRef.current && quote.deal_id && !quote.company_id) {
      dealAppliedRef.current = true;
      applyDeal(quote.deal_id);
    }
  }, [quote.deal_id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Always load the quote FRESH from the server (no react-query cache) — a stale
  // cached copy being auto-saved back was silently reverting quote values.
  const [existing, setExisting] = useState(null);
  useEffect(() => {
    if (!quoteId || loadedRef.current) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${quoteId}`);
        if (alive) setExisting(data);
      } catch {
        toast.error("Could not load quote — check your connection");
      }
    })();
    return () => { alive = false; };
  }, [quoteId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (existing && !loadedRef.current) {
      loadedRef.current = true;
      applyingRef.current = true;
      const q = { ...emptyQuote(), ...existing };
      // Coerce null string fields to '' so React inputs stay controlled.
      for (const k of ["customer_email", "customer_phone", "site_address", "company_id",
                        "contact_id", "deal_id", "scope_summary", "notes"]) {
        if (q[k] == null) q[k] = "";
      }
      // If it was a legacy V1 quote, keep as-is but flag
      if (!q.version || q.version < 2) q.version = 1;
      // Iter-66: normalise into `lines[]`. If the saved quote already has
      // lines, use them; otherwise wrap the legacy flat sections into a
      // single implicit "Fencing works" line so no data is lost.
      if (Array.isArray(existing.lines) && existing.lines.length > 0) {
        q.lines = existing.lines.map((l) => ({
          id: l.id || (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)),
          title: l.title || "",
          scope_narrative: l.scope_narrative || "",
          show_sections_to_customer: !!l.show_sections_to_customer,
          sections: Array.isArray(l.sections) ? l.sections : [],
          cost_total: Number(l.cost_total) || 0,
          ai_meta: l.ai_meta || null,
        }));
      } else if (Array.isArray(existing.sections) && existing.sections.length > 0) {
        q.lines = [{
          ...newLineItem("Fencing works"),
          scope_narrative: q.scope_summary || "",
          sections: existing.sections,
        }];
      } else if (q.version === 1) {
        // Legacy V1 (line_items only) — no lines needed, keep an empty
        // default so the UI still has something to render for the (rare)
        // case someone converts to sections.
        q.lines = [newLineItem("Fencing works")];
      } else {
        q.lines = [newLineItem("Fencing works")];
      }
      // Mirror flat sections for downstream consumers.
      q.sections = flatSections(q.lines);
      setQuote(q);
    }
  }, [existing]);

  const isLegacy = quote.version === 1 && (quote.line_items?.length || 0) > 0 && (quote.sections?.length || 0) === 0;

  // Totals (matches backend compute_totals)
  const totals = useMemo(() => {
    let cost = 0;
    for (const s of quote.sections || []) {
      const unit = (s.unit || "LM").toUpperCase();
      const rate = parseFloat(s.rate) || 0;
      const qty = parseFloat(s.qty) || 0;
      if (unit === "LM") {
        const len = parseFloat(s.length_m) || 0;
        cost += rate * len * Math.max(qty || 1, 1);
      } else if (s.section_type === "daily") {
        const units = Math.max(parseFloat(s.units) || 1, 1);
        cost += rate * units * Math.max(qty, 0);
      } else {
        cost += rate * qty;
      }
    }
    if (isLegacy) {
      cost = (quote.line_items || []).reduce((sum, it) => sum + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);
    }
    const gp = Math.min(parseFloat(quote.gp_margin_percent) || 0, 99);
    const sale = gp < 100 ? cost / (1 - gp / 100) : cost;
    const gpDollars = sale - cost;
    return { cost, sale, gp, gpDollars };
  }, [quote.sections, quote.line_items, quote.gp_margin_percent, isLegacy]);

  const buildPayload = (q) => ({
    ...q,
    version: 2,
    company_id: q.company_id || null,
    contact_id: q.contact_id || null,
    deal_id: q.deal_id || null,
    gp_margin_percent: parseFloat(q.gp_margin_percent) || DEFAULT_GP,
    linear_meters: (q.sections || []).filter((s) => s.section_type === "fence").reduce((sum, s) => sum + (parseFloat(s.length_m) || 0), 0),
    square_meters: 0,
  });

  const saveQuote = async ({ silent = false } = {}) => {
    const snapshot = quoteRef.current;
    // Locked quotes (pending/approved/sent) can't be saved — backend 409s.
    const snapAppr = snapshot.approval_status || ((snapshot.status === "sent" || snapshot.status === "accepted") ? "approved" : "draft");
    if (quoteId && snapAppr !== "draft" && snapAppr !== "changes_requested") {
      return { id: quoteId, quote_number: snapshot.quote_number };
    }
    setSaving(true);
    savingRef.current = true;
    try {
      let data;
      if (quoteId) {
        data = (await api.patch(`/quotes/${quoteId}`, buildPayload(snapshot))).data;
      } else {
        data = (await api.post("/quotes", buildPayload(snapshot))).data;
        loadedRef.current = true;
        setQuoteId(data.id);
        window.history.replaceState(null, "", `/quotes/v2/${data.id}`);
        applyingRef.current = true;
        setQuote((q) => ({ ...q, id: data.id, quote_number: data.quote_number }));
      }
      if (quoteRef.current === snapshot) dirtyRef.current = false;
      setAutoState(`Draft saved ${new Date().toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}`);
      qc.invalidateQueries({ queryKey: ["quotes"] });
      if (!silent) toast.success(quoteId ? "Quote updated" : `Quote ${data.quote_number} saved`);
      return data;
    } catch (e) {
      if (silent) setAutoState("Auto-save failed — check details");
      else toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  // Auto-save: any edit marks the draft dirty; a 2.5s pause persists it silently.
  useEffect(() => {
    if (applyingRef.current) { applyingRef.current = false; return; }
    dirtyRef.current = true;
    // No autosave while the quote is locked in the approval workflow.
    const apprSt = quote.approval_status || ((quote.status === "sent" || quote.status === "accepted") ? "approved" : "draft");
    if (quoteId && apprSt !== "draft" && apprSt !== "changes_requested") return;
    const eligible = (quote.customer_name || "").trim() && (quoteId || (quote.sections || []).length > 0);
    if (!eligible) return;
    const timer = setTimeout(() => {
      if (!dirtyRef.current || savingRef.current) return;
      setAutoState("saving");
      saveQuote({ silent: true }).catch(() => {});
    }, 2500);
    return () => clearTimeout(timer);
  }, [quote]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep quote.fence_type in sync with the FIRST fence section — the top-level
  // field is what downstream consumers (client portal fallback text, PDF, Xero
  // line description, job scope-of-works) all read. Without this sync, they'd
  // all show the stale "colorbond" default regardless of what the sections say.
  useEffect(() => {
    const secs = quote.sections || [];
    const primary = secs.find((s) => s.section_type === "fence" && s.fence_type);
    const derived = primary?.fence_type || "";
    if (derived && derived !== quote.fence_type) {
      applyingRef.current = true;   // don't mark dirty for this passive sync
      setQuote((q) => ({ ...q, fence_type: derived }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.sections]);

  const [aiScope, setAiScope] = useState(false);
  const draftScope = async () => {
    setAiScope(true);
    try {
      const secs = quote.sections || [];
      // Prefer the actual section-level fence types — the quote-level default
      // is "colorbond" for legacy V1 compatibility and doesn't reflect what's
      // in the sections.
      const uniqueFenceTypes = Array.from(new Set(
        secs.filter((s) => s.section_type === "fence" && s.fence_type).map((s) => s.fence_type)
      ));
      const primaryFenceType = uniqueFenceTypes[0]
        || quote.fence_type
        || "mixed";
      const { data } = await api.post("/ai/scope-summary", {
        fence_type: primaryFenceType,
        linear_meters: secs.filter((s) => s.section_type === "fence").reduce((sum, s) => sum + (parseFloat(s.length_m) || 0), 0),
        square_meters: 0,
        customer_name: quote.customer_name || null,
        site_address: quote.site_address || null,
        line_items: [],
        sections: secs.map((s) => ({
          section_type: s.section_type, label: s.label, unit: s.unit, qty: s.qty,
          length_m: s.length_m, height_mm: s.height_mm, colour: s.colour,
          fence_type: s.fence_type, gate_style: s.gate_style, notes: s.notes,
        })),
        measure_up: quote.notes || null,
      });
      setQuote((q) => ({ ...q, scope_summary: data.summary }));
      toast.success("Scope drafted — review & edit before sending");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafting failed — try again");
    } finally {
      setAiScope(false);
    }
  };

  const share = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${quoteId}/share`)).data,
    onSuccess: async (data) => {
      const url = `${window.location.origin}/portal/quote/${data.quote_id}?t=${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Client link copied to clipboard — paste into an email or SMS.");
      } catch {
        toast.success("Client link generated (clipboard blocked): " + url);
      }
      // Open the portal in a new tab for immediate preview (flagged so it doesn't ping "quote viewed")
      window.open(`${url}&preview=1`, "_blank", "noopener");
      // Refresh the LivePreviewPanel's share-audit + share button cache.
      setShareResetKey((k) => k + 1);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not generate link"),
  });

  const revokeShare = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${quoteId}/share/revoke`)).data,
    onSuccess: () => {
      toast.success("All previous share links revoked — old links now show 'expired'. Send a fresh Client Link if you still want the client to view the quote.");
      setShareResetKey((k) => k + 1);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not revoke share links"),
  });

  const sendQuote = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${quoteId}/send`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["quotes"] });
      setQuote((q) => ({ ...q, status: q.status === "draft" ? "sent" : q.status }));
      if (data.mocked) toast.warning(`Email to ${data.email} logged only — add the Resend API key to send for real.`);
      else toast.success(`Quote PDF + portal link emailed to ${data.email}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not send quote"),
  });

  // ---- Approval workflow mutations (Trent, Jun 2026) ----
  const applyWorkflow = (data) => {
    applyingRef.current = true;
    setQuote((q) => ({
      ...q, approval_status: data.approval_status, revision: data.revision ?? q.revision,
      status: data.status ?? q.status,
      changes_requested_by_name: data.changes_requested_by_name ?? null,
      changes_requested_at: data.changes_requested_at ?? null,
      changes_requested_comment: data.changes_requested_comment ?? null,
      approval_trail: data.approval_trail ?? q.approval_trail,
    }));
    qc.invalidateQueries({ queryKey: ["quotes"] });
  };
  const [reqChangesOpen, setReqChangesOpen] = useState(false);
  const [reqComment, setReqComment] = useState("");
  const [resubmitOpen, setResubmitOpen] = useState(false);
  const [resubmitNote, setResubmitNote] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitNote, setSubmitNote] = useState("");
  const [dealPeekOpen, setDealPeekOpen] = useState(false);
  const submitApproval = useMutation({
    mutationFn: async (note) => {
      await saveQuote({ silent: true });
      return (await api.post(`/quotes/${quoteId}/submit-approval`, note ? { note } : {})).data;
    },
    onSuccess: (data) => {
      applyWorkflow(data); setResubmitOpen(false); setResubmitNote(""); setSubmitOpen(false); setSubmitNote("");
      const last = (data.approval_trail || [])[(data.approval_trail || []).length - 1];
      toast.success(last?.event === "resubmitted"
        ? "Resubmitted for approval — the quote is locked again until it's approved"
        : "Submitted for approval — the quote is now locked until it's approved or revised");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not submit for approval"),
  });
  const requestChanges = useMutation({
    mutationFn: async (comment) => (await api.post(`/quotes/${quoteId}/request-changes`, { comment })).data,
    onSuccess: (data) => {
      applyWorkflow(data); setReqChangesOpen(false); setReqComment("");
      toast.success("Sent back for changes — the submitter can now edit and resubmit");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not request changes"),
  });
  const approveQuote = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${quoteId}/approve`)).data,
    onSuccess: (data) => { applyWorkflow(data); toast.success("Quote approved — Complete & Send is now unlocked"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not approve quote"),
  });
  const reviseQuote = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${quoteId}/revise`)).data,
    onSuccess: (data) => { applyWorkflow(data); toast.success("Quote unlocked for editing — re-submit for approval when ready."); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not revise quote"),
  });

  // "Mark as Sent" (manual) — for quotes processed outside Compass (e.g. sent
  // directly through Xero or by another channel). Does NOT email the client;
  // just flips the status so the quote drops out of Draft lists, fires the
  // `quote_sent` automation event, and stamps sent_source="manual".
  const markSent = useMutation({
    mutationFn: async () => (await api.patch(`/quotes/${quoteId}`, { status: "sent" })).data,
    onSuccess: (data) => {
      setQuote((q) => ({ ...q, ...data, status: "sent" }));
      qc.invalidateQueries({ queryKey: ["quotes"] });
      setMarkSentOpen(false);
      toast.success("Quote marked as sent — client wasn't emailed from Compass.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not mark as sent"),
  });

  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [markSentOpen, setMarkSentOpen] = useState(false);
  const confirmSend = async () => {
    try {
      await saveQuote({ silent: true });
      await sendQuote.mutateAsync();
    } catch (e) {
      console.warn("quote send failed (error already toasted)", e);
    }
    setConfirmSendOpen(false);
  };

  const openPreview = async () => {
    if (!quote.customer_name) { toast.error("Customer name required"); return; }
    if (quote.sections.length === 0 && !isLegacy) { toast.error("Add at least one section"); return; }
    setPreviewLoading(true);
    try {
      const saved = await saveQuote({ silent: true });
      const qid = quoteId || saved.id;
      const res = await api.get(`/quotes/${qid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
      setPreviewOpen(true);
    } catch (e) {
      // Surface the real backend detail if there is one; if the failure was
      // a client-side save guard (thrown Error without an axios response),
      // show that message too so the user knows what to fix.
      const detail = e?.response?.data?.detail
        || e?.message
        || "Could not build preview — check backend logs";
      toast.error(detail);
    } finally {
      setPreviewLoading(false);
    }
  };

  const [downloadingPreview, setDownloadingPreview] = useState(false);
  // Bumps whenever a share is created or revoked — LivePreviewPanel uses
  // this to reset its cached share URL and refresh the audit log.
  const [shareResetKey, setShareResetKey] = useState(0);
  const downloadPreview = async () => {
    if (!quote.customer_name) { toast.error("Add a customer name first — needed for the file name."); return; }
    if (quote.sections.length === 0 && !isLegacy) { toast.error("Add at least one section before downloading."); return; }
    setDownloadingPreview(true);
    try {
      // Auto-save silently so the PDF matches what's on screen and gets a quote number.
      const saved = await saveQuote({ silent: true });
      const qid = quoteId || saved.id;
      const res = await api.get(`/quotes/${qid}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Prefer the server-issued quote number (e.g. "CCFI-Q-1040") — it already
      // starts with "CCFI-", so use it verbatim. Fall back to customer name.
      const baseName = (saved.quote_number || `CCFI-${quote.customer_name}`).replace(/[^\w-]+/g, "-");
      a.download = `${baseName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Preview PDF downloaded — draft saved.");
    } catch (e) {
      const detail = e?.response?.data?.detail
        || e?.message
        || "Could not build preview — check backend logs";
      toast.error(detail);
    } finally {
      setDownloadingPreview(false);
    }
  };

  // Preview → jump-to-section. Smooth-scrolls to the section card in the main
  // form and briefly flashes it cyan so the user's eye lands on it.
  const scrollToSection = (sid) => {
    if (!sid) return;
    const el = document.getElementById(`section-${sid}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("ring-2", "ring-cyan-400", "ring-offset-2", "transition-all");
    setTimeout(() => {
      el.classList.remove("ring-2", "ring-cyan-400", "ring-offset-2");
    }, 1600);
  };

  const nextLetter = () => SECTION_LETTERS[quote.sections.length] || `#${quote.sections.length + 1}`;

  // -------------------- Iter-66: line-scoped mutations --------------------
  // All writes flow through `updateLines`. It updates lines[] AND mirrors
  // the flat sections[] view so downstream consumers (LivePreview, totals,
  // fence-type sync, RFQ, MaterialEditDialog) keep working unchanged.
  const updateLines = (updater) => {
    setQuote((q) => {
      const nextLines = updater(q.lines || []);
      return { ...q, lines: nextLines, sections: flatSections(nextLines) };
    });
  };

  const addLine = () => {
    updateLines((lines) => [...lines, newLineItem(`Line ${lines.length + 1}`)]);
  };

  const updateLine = (lineIdx, patch) => {
    updateLines((lines) => lines.map((l, i) => (i === lineIdx ? { ...l, ...patch } : l)));
  };

  const duplicateLine = (lineIdx) => {
    updateLines((lines) => {
      const src = lines[lineIdx];
      if (!src) return lines;
      const rnd = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
      const clone = {
        ...src,
        id: rnd(),
        title: `${src.title || "Line item"} (copy)`,
        sections: (src.sections || []).map((s) => ({ ...s, id: rnd() })),
        ai_meta: null,
      };
      return [...lines.slice(0, lineIdx + 1), clone, ...lines.slice(lineIdx + 1)];
    });
  };

  const removeLine = (lineIdx) => {
    updateLines((lines) => {
      if ((lines || []).length <= 1) {
        // Never leave zero lines — reset the single remaining one so the
        // builder always has a Line 1 target for new sections.
        return [newLineItem("Fencing works")];
      }
      return lines.filter((_, i) => i !== lineIdx);
    });
  };

  const addSectionToLine = (lineIdx, partial) => {
    const isFenceSection = (partial?.section_type || "fence") === "fence";
    const base = {
      id: crypto.randomUUID(),
      section_type: "fence",
      label: `Section ${nextLetter()}`,
      unit: "LM",
      rate: 0,
      qty: 1,
      notes: "",
    };
    if (isFenceSection) {
      Object.assign(base, {
        fence_type: "other",
        height_mm: 1800,
        length_m: 0,
        colour: null,
        colour_hex: null,
      });
    }
    updateLines((lines) => lines.map((l, i) => (i === lineIdx ? {
      ...l,
      sections: [...(l.sections || []), { ...base, ...partial }],
      ai_meta: null,  // sections changed — narrative is now stale
    } : l)));
    setAddOpen(null);
  };

  const updateSectionInLine = (lineIdx, sectionIdx, patch) => {
    updateLines((lines) => lines.map((l, i) => {
      if (i !== lineIdx) return l;
      const nextSecs = (l.sections || []).map((s, j) => (j === sectionIdx ? { ...s, ...patch } : s));
      return { ...l, sections: nextSecs };
    }));
  };

  const duplicateSectionInLine = (lineIdx, sectionIdx) => {
    updateLines((lines) => lines.map((l, i) => {
      if (i !== lineIdx) return l;
      const src = (l.sections || [])[sectionIdx];
      if (!src) return l;
      const secs = [...(l.sections || [])];
      secs.splice(sectionIdx + 1, 0, { ...src, id: crypto.randomUUID(), label: `${src.label} (copy)` });
      return { ...l, sections: secs, ai_meta: null };
    }));
  };

  const removeSectionFromLine = (lineIdx, sectionIdx) => {
    updateLines((lines) => lines.map((l, i) => (i === lineIdx ? {
      ...l,
      sections: (l.sections || []).filter((_, j) => j !== sectionIdx),
      ai_meta: null,
    } : l)));
  };

  const openFab = (lineIdx, editIndex = null) => {
    setFabDraft(null);
    setFabDialog({ open: true, lineIdx, editIndex });
  };

  const commitFab = () => {
    if (!fabDraft?.result) { toast.error("Adjust the fabrication details first"); return; }
    const inp = fabDraft.input, r = fabDraft.result;
    const { lineIdx, editIndex } = fabDialog;
    if (lineIdx == null) { toast.error("No line item selected"); return; }
    const editing = editIndex != null;
    const currentSection = editing ? (quote.lines?.[lineIdx]?.sections || [])[editIndex] : null;
    const sec = {
      id: editing && currentSection ? currentSection.id : crypto.randomUUID(),
      section_type: "fabrication",
      label: (inp.notes && inp.notes.trim()) || r.summary || "Fabrication",
      unit: "each", qty: 1, rate: r.cost_total || 0,
      fabrication: inp, source: "fabrication", notes: inp.notes || "",
    };
    if (editing) updateSectionInLine(lineIdx, editIndex, sec);
    else updateLines((lines) => lines.map((l, i) => (i === lineIdx ? {
      ...l,
      sections: [...(l.sections || []), sec],
      ai_meta: null,
    } : l)));
    setFabDialog({ open: false, lineIdx: null, editIndex: null });
    setFabDraft(null);
  };

  // Iter-66: per-line AI narrative. Sends `mode="line"` + this line's own
  // sub-sections so the AI writes a tight 2-3 sentence customer-facing
  // description scoped to THIS line only.
  const [aiLineBusy, setAiLineBusy] = useState(null); // lineIdx | null
  const draftLineScope = async (lineIdx) => {
    const line = (quote.lines || [])[lineIdx];
    if (!line) return;
    const secs = line.sections || [];
    if (secs.length === 0) {
      toast.error("Add at least one sub-section (fence / gate / labour…) before generating.");
      return;
    }
    setAiLineBusy(lineIdx);
    try {
      const primaryFenceType = secs.filter((s) => s.section_type === "fence" && s.fence_type)[0]?.fence_type
        || quote.fence_type || "mixed";
      const { data } = await api.post("/ai/scope-summary", {
        mode: "line",
        line_title: line.title || "Fencing works",
        fence_type: primaryFenceType,
        linear_meters: secs.filter((s) => s.section_type === "fence").reduce((sum, s) => sum + (parseFloat(s.length_m) || 0), 0),
        square_meters: 0,
        customer_name: quote.customer_name || null,
        site_address: quote.site_address || null,
        line_items: [],
        sections: secs.map((s) => ({
          section_type: s.section_type, label: s.label, unit: s.unit, qty: s.qty,
          length_m: s.length_m, height_mm: s.height_mm, colour: s.colour,
          fence_type: s.fence_type, gate_style: s.gate_style, notes: s.notes,
        })),
        measure_up: quote.notes || null,
      });
      updateLine(lineIdx, {
        scope_narrative: data.summary,
        ai_meta: { ...(data.ai_meta || {}), source_fp: _sectionsFingerprint(secs) },
      });
      toast.success(`Line ${lineIdx + 1} scope drafted — review & edit before sending`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafting failed — try again");
    } finally {
      setAiLineBusy(null);
    }
  };

  // -------------------- Legacy shims for downstream code --------------------
  // Some callers (SitePlanReader import, older effect flows) still use the
  // old flat-section names. Keep 1:1 wrappers so those paths keep working
  // without a wider rewrite.
  const addSection = (partial) => {
    const lineIdx = addOpen?.lineIdx ?? 0;
    addSectionToLine(lineIdx, partial);
  };
  const updateSection = (idx, patch) => {
    // idx here refers to the FLAT section index — convert to (line, section).
    const { lineIdx, sectionIdx } = _flatIndexToLine(quote.lines, idx);
    if (lineIdx == null) return;
    updateSectionInLine(lineIdx, sectionIdx, patch);
  };
  const removeSection = (idx) => {
    const { lineIdx, sectionIdx } = _flatIndexToLine(quote.lines, idx);
    if (lineIdx == null) return;
    removeSectionFromLine(lineIdx, sectionIdx);
  };
  const duplicateSection = (idx) => {
    const { lineIdx, sectionIdx } = _flatIndexToLine(quote.lines, idx);
    if (lineIdx == null) return;
    duplicateSectionInLine(lineIdx, sectionIdx);
  };

  return (
    <div>
      <PageHeader
        title={quoteId ? (
          <span className="inline-flex items-center gap-3 flex-wrap">
            <span>{quote.quote_number || "Edit Quote"}</span>
            {revision >= 2 && (
              <Badge data-testid="quote-revision-badge" variant="outline" className="rounded-sm text-xs font-semibold border-slate-300 text-slate-600 px-2 py-0.5">
                Rev {revision}
              </Badge>
            )}
            {approval === "pending_approval" && (
              <Badge data-testid="quote-pending-badge" className="bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100 rounded-sm text-xs font-semibold tracking-wide px-2 py-0.5">
                <Lock size={12} className="mr-1" /> Pending approval
              </Badge>
            )}
            {approval === "changes_requested" && (
              <Badge data-testid="quote-changes-badge" className="bg-rose-100 text-rose-800 border border-rose-300 hover:bg-rose-100 rounded-sm text-xs font-semibold tracking-wide px-2 py-0.5">
                <Undo2 size={12} className="mr-1" /> Changes requested
              </Badge>
            )}
            {approval === "approved" && quote.status !== "sent" && quote.status !== "accepted" && (
              <Badge data-testid="quote-approved-badge" className="bg-sky-100 text-sky-800 border border-sky-300 hover:bg-sky-100 rounded-sm text-xs font-semibold tracking-wide px-2 py-0.5">
                <ShieldCheck size={12} className="mr-1" /> Approved
              </Badge>
            )}
            {quote.status === "sent" && (
              <Badge data-testid="quote-sent-badge" className="bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 rounded-sm text-xs font-semibold tracking-wide px-2 py-0.5">
                <CheckCircle2 size={12} className="mr-1" /> Sent
              </Badge>
            )}
          </span>
        ) : "Build Quote (V2)"}
        subtitle="Multi-section quote — auto-saves as you build. Preview before sending; nothing goes to the client until you press Complete & Send."
        breadcrumb={["Sales", "Quotes", quoteId ? "Edit" : "New"]}
        actions={
          <>
            <span className="hidden md:inline-flex items-center text-[11px] font-mono text-slate-400 mr-1" data-testid="autosave-state">
              {autoState === "saving" ? "Saving…" : autoState}
            </span>

            {/* --- DESKTOP: all actions inline (md+) --- */}
            <div className="hidden md:contents">
              <Button variant="outline" onClick={() => goBack(nav, "/quotes")} className="rounded-sm h-10">
                <ArrowLeft size={16} className="mr-1" /> Back
              </Button>
              {quote.deal_id && (
                <Button
                  data-testid="view-deal-btn"
                  onClick={() => setDealPeekOpen((v) => !v)}
                  variant="outline"
                  title="Review the deal — requirements, photos and scoping files — in a floating panel"
                  className={`rounded-sm h-10 ${dealPeekOpen ? "border-amber-500 bg-amber-50 text-amber-800" : "border-amber-400 text-amber-700 hover:bg-amber-50"}`}
                >
                  <Briefcase size={16} className="mr-2" /> View deal
                </Button>
              )}
              {quoteId && approval === "approved" && (
                <Button
                  data-testid="send-to-client-btn"
                  onClick={() => share.mutate()}
                  disabled={share.isPending}
                  variant="outline"
                  className="rounded-sm h-10 border-cyan-500 text-cyan-700 hover:bg-cyan-50"
                >
                  {share.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Link2 size={16} className="mr-2" />}
                  Client Link
                </Button>
              )}
              {quoteId && (
                <Button
                  data-testid="revoke-share-btn"
                  onClick={() => {
                    if (window.confirm("Revoke every existing client link for this quote? Anyone with an old link will see 'Invalid or expired'. You can send a fresh link afterwards.")) {
                      revokeShare.mutate();
                    }
                  }}
                  disabled={revokeShare.isPending}
                  variant="outline"
                  title="Revoke all outstanding share links for this quote"
                  className="rounded-sm h-10 border-rose-500 text-rose-700 hover:bg-rose-50"
                >
                  {revokeShare.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <ShieldOff size={16} className="mr-2" />}
                  Revoke Links
                </Button>
              )}
              {quoteId && (
                <Button
                  data-testid="duplicate-quote-btn"
                  onClick={() => setDupOpen(true)}
                  variant="outline"
                  title="Copy this quote to a new standalone draft"
                  className="rounded-sm h-10 border-slate-400 text-slate-700 hover:bg-slate-100"
                >
                  <Copy size={16} className="mr-2" /> Duplicate
                </Button>
              )}
              <Button
                data-testid="preview-quote-btn"
                onClick={openPreview}
                disabled={previewLoading || saving}
                variant="outline"
                className="rounded-sm h-10 border-slate-400 text-slate-700 hover:bg-slate-100"
              >
                {previewLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Eye size={16} className="mr-2" />}
                Preview &amp; Save Draft
              </Button>
              {quoteId && (approval === "draft" || approval === "changes_requested") && quote.status !== "accepted" && (
                <Button
                  data-testid="submit-approval-btn"
                  onClick={() => { if (approval === "changes_requested") { setResubmitOpen(true); } else { setSubmitNote(""); setSubmitOpen(true); } }}
                  disabled={submitApproval.isPending || saving}
                  className="rounded-sm h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {submitApproval.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <ShieldCheck size={16} className="mr-2" />}
                  {approval === "changes_requested" ? "Resubmit for approval" : "Submit for approval"}
                </Button>
              )}
              {quoteId && approval === "pending_approval" && !isApprover && (
                <Button data-testid="pending-approval-btn" disabled variant="outline"
                  className="rounded-sm h-10 border-amber-400 text-amber-700 bg-amber-50 opacity-100">
                  <Lock size={16} className="mr-2" /> Pending approval
                </Button>
              )}
              {quoteId && approval === "pending_approval" && isApprover && (
                <>
                  <Button
                    data-testid="approve-quote-btn"
                    onClick={() => approveQuote.mutate()}
                    disabled={approveQuote.isPending}
                    className="rounded-sm h-10 bg-sky-600 hover:bg-sky-700 text-white"
                  >
                    {approveQuote.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <ShieldCheck size={16} className="mr-2" />}
                    Approve quote
                  </Button>
                  <Button
                    data-testid="request-changes-btn"
                    onClick={() => { setReqComment(""); setReqChangesOpen(true); }}
                    disabled={requestChanges.isPending}
                    className="rounded-sm h-10 bg-rose-600 hover:bg-rose-700 text-white"
                  >
                    <Undo2 size={16} className="mr-2" /> Request changes
                  </Button>
                </>
              )}
              {quoteId && approval === "approved" && quote.status !== "accepted" && (
                <Button
                  data-testid="email-quote-btn"
                  onClick={async () => {
                    if (!quote.customer_name) { toast.error("Customer name required"); return; }
                    if (!quote.customer_email) { toast.error("Add the customer's email in the quote details first"); return; }
                    setConfirmSendOpen(true);
                  }}
                  disabled={sendQuote.isPending || saving}
                  className="rounded-sm h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Send size={16} className="mr-2" />
                  Complete &amp; Send
                </Button>
              )}
              {quoteId && approval === "approved" && quote.status !== "accepted" && quote.status !== "sent" && (
                <Button
                  data-testid="mark-sent-btn"
                  onClick={() => setMarkSentOpen(true)}
                  disabled={markSent.isPending || saving}
                  variant="outline"
                  title="Mark this quote as sent without emailing — use when you've sent it manually"
                  className="rounded-sm h-10 border-slate-500 text-slate-700 hover:bg-slate-100"
                >
                  {markSent.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <CheckCircle2 size={16} className="mr-2" />}
                  Mark as Sent
                </Button>
              )}
              {quoteId && quote.status === "sent" && (
                <Button
                  data-testid="mark-accepted-btn"
                  onClick={() => setAcceptOpen(true)}
                  disabled={saving}
                  className="rounded-sm h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  <CheckCircle2 size={16} className="mr-2" /> Mark accepted
                </Button>
              )}
              {quoteId && locked && quote.status !== "accepted" && (
                <Button
                  data-testid="revise-quote-btn"
                  variant="outline"
                  onClick={() => reviseQuote.mutate()}
                  disabled={reviseQuote.isPending}
                  title="Unlock for editing — resets approval; the revision only bumps if the quote was already sent to the client"
                  className="rounded-sm h-10 border-amber-500 text-amber-700 hover:bg-amber-50"
                >
                  {reviseQuote.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Undo2 size={16} className="mr-2" />}
                  Revise quote
                </Button>
              )}
              {quoteId && quote.status === "accepted" && (
                <Badge variant="outline" className="rounded-sm h-10 px-3 flex items-center border-emerald-300 bg-emerald-50 text-emerald-700 font-semibold" data-testid="quote-accepted-badge">
                  <CheckCircle2 size={14} className="mr-1.5" /> ACCEPTED
                </Badge>
              )}
            </div>

            {/* --- MOBILE: primary Save + overflow menu (below md) --- */}
            <div className="flex md:hidden items-center gap-2 w-full">
              {quote.deal_id && (
                <Button
                  data-testid="view-deal-btn-mobile"
                  onClick={() => setDealPeekOpen((v) => !v)}
                  variant="outline"
                  aria-label="View deal"
                  className={`rounded-sm h-11 w-11 p-0 ${dealPeekOpen ? "border-amber-500 bg-amber-50 text-amber-800" : "border-amber-400 text-amber-700"}`}
                >
                  <Briefcase size={16} />
                </Button>
              )}
              {!locked && (
                <Button
                  data-testid="save-quote-v2-btn-mobile"
                  onClick={() => {
                    if (!quote.customer_name) { toast.error("Customer name required"); return; }
                    if (quote.sections.length === 0 && !isLegacy) { toast.error("Add at least one section"); return; }
                    saveQuote().catch(() => {});
                  }}
                  disabled={saving}
                  className="flex-1 rounded-sm h-11 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold"
                >
                  {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                  {!quoteId || quote.status === "draft" ? "Save draft" : "Update quote"}
                </Button>
              )}
              {locked && approval === "pending_approval" && isApprover && (
                <>
                  <Button data-testid="approve-quote-btn-mobile" onClick={() => approveQuote.mutate()} disabled={approveQuote.isPending}
                    className="flex-1 rounded-sm h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold">
                    {approveQuote.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <ShieldCheck size={16} className="mr-2" />} Approve quote
                  </Button>
                  <Button data-testid="request-changes-btn-mobile" onClick={() => { setReqComment(""); setReqChangesOpen(true); }}
                    className="rounded-sm h-11 w-11 p-0 bg-rose-600 hover:bg-rose-700 text-white" aria-label="Request changes">
                    <Undo2 size={16} />
                  </Button>
                </>
              )}
              {locked && approval === "pending_approval" && !isApprover && (
                <Button data-testid="pending-approval-btn-mobile" disabled variant="outline"
                  className="flex-1 rounded-sm h-11 border-amber-400 text-amber-700 bg-amber-50 font-semibold opacity-100">
                  <Lock size={16} className="mr-2" /> Pending approval
                </Button>
              )}
              {locked && approval === "approved" && quote.status !== "accepted" && quote.status !== "sent" && (
                <Button data-testid="pdf-approved-note-mobile" disabled variant="outline"
                  className="flex-1 rounded-sm h-11 border-sky-300 text-sky-700 bg-sky-50 font-semibold opacity-100">
                  <ShieldCheck size={16} className="mr-2" /> Approved
                </Button>
              )}
              {locked && quote.status === "sent" && (
                <Button data-testid="mark-accepted-btn-mobile" onClick={() => setAcceptOpen(true)}
                  className="flex-1 rounded-sm h-11 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold">
                  <CheckCircle2 size={16} className="mr-2" /> Mark accepted
                </Button>
              )}
              {quoteId && approval === "approved" && quote.status !== "accepted" && (
                <Button
                  data-testid="email-quote-btn-mobile"
                  onClick={async () => {
                    if (!quote.customer_name) { toast.error("Customer name required"); return; }
                    if (!quote.customer_email) { toast.error("Add the customer's email in the quote details first"); return; }
                    setConfirmSendOpen(true);
                  }}
                  disabled={sendQuote.isPending || saving}
                  className="rounded-sm h-11 w-11 p-0 bg-emerald-600 hover:bg-emerald-700 text-white"
                  aria-label="Complete and send quote"
                >
                  <Send size={16} />
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-sm h-11 w-11 p-0" data-testid="quote-actions-menu-btn" aria-label="More actions">
                    <MoreHorizontal size={18} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" data-testid="quote-actions-menu">
                  {autoState && (
                    <>
                      <DropdownMenuLabel className="text-[11px] font-mono text-slate-400 font-normal">
                        {autoState === "saving" ? "Saving…" : autoState}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {quoteId && quote.status === "accepted" && (
                    <>
                      <DropdownMenuLabel className="text-emerald-700 font-semibold">
                        <CheckCircle2 size={13} className="inline mr-1.5" /> ACCEPTED
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {quoteId && (approval === "draft" || approval === "changes_requested") && quote.status !== "accepted" && (
                    <DropdownMenuItem onClick={() => { if (approval === "changes_requested") { setResubmitOpen(true); } else { setSubmitNote(""); setSubmitOpen(true); } }} disabled={submitApproval.isPending} className="text-emerald-800" data-testid="submit-approval-menu">
                      <ShieldCheck size={13} className="mr-2" /> {approval === "changes_requested" ? "Resubmit for approval" : "Submit for approval"}
                    </DropdownMenuItem>
                  )}
                  {quoteId && locked && quote.status !== "accepted" && (
                    <DropdownMenuItem onClick={() => reviseQuote.mutate()} disabled={reviseQuote.isPending} className="text-amber-700" data-testid="revise-quote-menu">
                      <Undo2 size={13} className="mr-2" /> Revise quote
                    </DropdownMenuItem>
                  )}
                  {quoteId && approval === "approved" && quote.status !== "accepted" && quote.status !== "sent" && (
                    <DropdownMenuItem onClick={() => setMarkSentOpen(true)} className="text-slate-800" data-testid="mark-sent-menu">
                      <CheckCircle2 size={13} className="mr-2" /> Mark as Sent (manual)
                    </DropdownMenuItem>
                  )}
                  {quoteId && quote.status === "sent" && (
                    <DropdownMenuItem onClick={() => setAcceptOpen(true)} className="text-emerald-800" data-testid="mark-accepted-menu">
                      <CheckCircle2 size={13} className="mr-2" /> Mark accepted
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={openPreview} disabled={previewLoading || saving} data-testid="preview-quote-menu">
                    {previewLoading ? <Loader2 size={13} className="animate-spin mr-2" /> : <Eye size={13} className="mr-2" />}
                    Preview &amp; Save Draft
                  </DropdownMenuItem>
                  {quote.deal_id && (
                    <DropdownMenuItem onClick={() => setDealPeekOpen((v) => !v)} className="text-amber-700" data-testid="view-deal-menu">
                      <Briefcase size={13} className="mr-2" /> View deal
                    </DropdownMenuItem>
                  )}
                  {quoteId && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">Client</DropdownMenuLabel>
                      {approval === "approved" && (
                      <DropdownMenuItem onClick={() => share.mutate()} disabled={share.isPending} className="text-cyan-700" data-testid="send-to-client-menu">
                        {share.isPending ? <Loader2 size={13} className="animate-spin mr-2" /> : <Link2 size={13} className="mr-2" />}
                        Client Link
                      </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => {
                          if (window.confirm("Revoke every existing client link for this quote? Anyone with an old link will see 'Invalid or expired'.")) {
                            revokeShare.mutate();
                          }
                        }}
                        disabled={revokeShare.isPending}
                        className="text-rose-700"
                        data-testid="revoke-share-menu"
                      >
                        <ShieldOff size={13} className="mr-2" /> Revoke Links
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => goBack(nav, "/quotes")} data-testid="back-to-quotes-menu">
                    <ArrowLeft size={13} className="mr-2" /> Back to quotes
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Desktop-only Save (mobile Save is inside the mobile flex row above) */}
            {!locked && (
              <Button
                data-testid="save-quote-v2-btn"
                onClick={() => {
                  if (!quote.customer_name) { toast.error("Customer name required"); return; }
                  if (quote.sections.length === 0 && !isLegacy) { toast.error("Add at least one section"); return; }
                  saveQuote().catch(() => {});
                }}
                disabled={saving}
                className="hidden md:inline-flex rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                {!quoteId || quote.status === "draft" ? "Save draft" : "Update quote"}
              </Button>
            )}
          </>
        }
      />

      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="confirm-accept-quote-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Mark quote as accepted?</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <p className="text-slate-600">Use this when the client has accepted verbally / by email instead of via the portal.</p>
            <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3 text-xs text-emerald-900 space-y-1">
              <div>This will automatically:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Move the linked deal to <b>Won</b></li>
                <li>Create the <b>install job</b> ({quote.job_number || "JN"}) with deposit tracking</li>
                <li>Generate the <b>material take-off</b>, blueprints &amp; fabrication items</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAcceptOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="confirm-accept-btn" disabled={accepting} onClick={async () => {
              setAccepting(true);
              try {
                await saveQuote({ silent: true }).catch(() => {});
                await api.patch(`/quotes/${quoteId}`, { status: "accepted" });
                setQuote((q) => ({ ...q, status: "accepted" }));
                setAcceptOpen(false);
                toast.success("Quote accepted — deal won, install job & materials pipeline kicked off");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not mark accepted");
              } finally {
                setAccepting(false);
              }
            }} className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white">
              {accepting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />} Yes — mark accepted
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Sent (manual) — for quotes processed outside Compass */}
      <Dialog open={markSentOpen} onOpenChange={(o) => !o && setMarkSentOpen(false)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="mark-sent-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <CheckCircle2 size={18} className="text-slate-600" /> Mark this quote as sent?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p className="text-slate-600">Use this when you&apos;ve <b>already sent the quote to the client outside Compass</b> — e.g. through Xero directly, over WhatsApp, or as a paper copy.</p>
            <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-xs text-amber-900">
              <div className="font-semibold mb-1">This will NOT:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Email the client from Compass</li>
                <li>Generate a PDF or portal link</li>
              </ul>
              <div className="font-semibold mt-2 mb-1">It WILL:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Move the quote out of Draft into <b>Sent</b></li>
                <li>Stamp <code>sent_at</code> = now, <code>sent_source</code> = <code>manual</code></li>
                <li>Fire the <code>quote_sent</code> automation (e.g. follow-up reminders)</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMarkSentOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="confirm-mark-sent-btn" onClick={() => markSent.mutate()} disabled={markSent.isPending}
              className="rounded-sm bg-slate-800 hover:bg-slate-900 text-white">
              {markSent.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
              Yes — mark as sent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DuplicateQuoteDialog
        quote={{ id: quoteId, quote_number: quote.quote_number, deal_id: quote.deal_id }}
        open={dupOpen}
        onClose={() => setDupOpen(false)}
      />

      <ConfirmSendQuoteDialog
        open={confirmSendOpen}
        quote={quote}
        onClose={async (sent) => {
          setConfirmSendOpen(false);
          if (sent) {
            setQuote((q) => ({ ...q, status: q.status === "draft" ? "sent" : q.status }));
            qc.invalidateQueries({ queryKey: ["quotes"] });
            qc.invalidateQueries({ queryKey: ["quote", quoteId] });
          }
        }}
      />

      {/* Request changes (approver push-back) */}
      <Dialog open={reqChangesOpen} onOpenChange={setReqChangesOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="request-changes-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Undo2 size={18} className="text-rose-600" /> Request changes — {quote.quote_number}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">Sends the quote back to the submitter for revision or clarification. It unlocks for editing and must be resubmitted for approval. The revision number is not changed.</p>
          <div>
            <div className="stamp-label text-slate-600 mb-1">What needs to change or be clarified? *</div>
            <Textarea data-testid="request-changes-comment" rows={4} value={reqComment}
              onChange={(e) => setReqComment(e.target.value)} maxLength={2000} className="rounded-sm text-sm"
              placeholder="e.g. GP margin looks too low for a rural job — check the footing allowance and travel time" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReqChangesOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="request-changes-send-btn"
              disabled={requestChanges.isPending || reqComment.trim().length < 3}
              onClick={() => requestChanges.mutate(reqComment.trim())}
              className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white">
              {requestChanges.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Undo2 size={14} className="mr-1.5" />}
              Send back for changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit for approval (optional message to the approver) — Iter-87 */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="submit-approval-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <ShieldCheck size={18} className="text-cyan-600" /> Submit {quote.quote_number} for approval
            </DialogTitle>
          </DialogHeader>
          <div>
            <div className="stamp-label text-slate-600 mb-1">Message to the approver (optional)</div>
            <Textarea data-testid="submit-approval-note" rows={3} value={submitNote}
              onChange={(e) => setSubmitNote(e.target.value)} maxLength={2000} className="rounded-sm text-sm"
              placeholder="e.g. Priced the gate hardware below our usual floor — need your call on margin" />
            <p className="text-[10.5px] text-slate-400 mt-1.5">Sent with the approval request — shows on the quote, in the approval trail and in the approver's notification. Internal only, never on the client PDF.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSubmitOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="submit-approval-send-btn" disabled={submitApproval.isPending}
              onClick={() => submitApproval.mutate(submitNote.trim() || "")}
              className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
              {submitApproval.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <ShieldCheck size={14} className="mr-1.5" />}
              Submit for approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resubmit for approval (optional note) */}
      <Dialog open={resubmitOpen} onOpenChange={setResubmitOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="resubmit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <ShieldCheck size={18} className="text-cyan-600" /> Resubmit {quote.quote_number} for approval
            </DialogTitle>
          </DialogHeader>
          {quote.changes_requested_comment && (
            <div className="border border-rose-200 bg-rose-50 rounded-sm px-3 py-2 text-xs text-slate-700 italic">
              “{quote.changes_requested_comment}” — {quote.changes_requested_by_name || "approver"}
            </div>
          )}
          <div>
            <div className="stamp-label text-slate-600 mb-1">Note to the approver (optional)</div>
            <Textarea data-testid="resubmit-note" rows={3} value={resubmitNote}
              onChange={(e) => setResubmitNote(e.target.value)} maxLength={2000} className="rounded-sm text-sm"
              placeholder="e.g. Footing allowance bumped to 14 holes + rock breaker day added" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResubmitOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="resubmit-send-btn" disabled={submitApproval.isPending}
              onClick={() => submitApproval.mutate(resubmitNote.trim() || "")}
              className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
              {submitApproval.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <ShieldCheck size={14} className="mr-1.5" />}
              Resubmit for approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview & save-draft dialog */}
      <Dialog open={previewOpen} onOpenChange={(o) => { setPreviewOpen(o); if (!o && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}>
        <DialogContent className="rounded-sm sm:max-w-3xl max-h-[94vh]" data-testid="preview-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Preview — saved as draft</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">This is the exact PDF the client receives. Your quote is safely saved as a <b>draft</b> — nothing has been sent yet.
            {previewUrl && (
              <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-cyan-600 underline ml-1" data-testid="preview-open-tab-link">Open PDF in new tab</a>
            )}
          </p>
          {previewUrl ? (
            <iframe title="Quote PDF preview" src={previewUrl} className="w-full h-[58vh] border border-slate-200 rounded-sm bg-slate-50" data-testid="preview-quote-iframe" />
          ) : (
            <div className="h-[58vh] flex items-center justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="rounded-sm" data-testid="preview-keep-editing-btn">Keep editing</Button>
            {approval === "approved" && quote.status !== "accepted" && (
            <Button
              data-testid="preview-send-btn"
              onClick={() => {
                if (!quote.customer_email) { toast.error("Add the customer's email in the quote details first"); return; }
                setPreviewOpen(false);
                setConfirmSendOpen(true);
              }}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Send size={14} className="mr-1.5" /> Complete &amp; Send
            </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6 grid lg:grid-cols-[1fr_360px] gap-4 sm:gap-6">
        <div className="space-y-4 sm:space-y-6 min-w-0">
          {locked && (
            <div data-testid="quote-locked-banner" className="border border-amber-300 bg-amber-50 rounded-sm p-3 flex items-center gap-3 flex-wrap">
              <Lock size={16} className="text-amber-600 shrink-0" />
              <div className="text-sm text-amber-900 flex-1 min-w-[200px]">
                <b>Locked — Rev {revision}</b>
                {" · "}
                {approval === "pending_approval" ? (
                  <>awaiting approval{pendingSubmitEv?.by_name ? <> — submitted by <b>{pendingSubmitEv.by_name}</b></> : null}</>
                ) : quote.status === "sent" ? "sent to the client" : quote.status === "accepted" ? "accepted" : "approved — ready to send"}.
                {quote.status !== "accepted" && <> Press <b>Revise quote</b> to edit{quote.status === "sent" ? " — it starts a new revision (the client has this version)" : " — restarts the approval (revision unchanged)"}.</>}
                {approval === "pending_approval" && pendingSubmitEv?.note && (
                  <div className="mt-1.5 bg-white border border-amber-200 rounded-sm px-3 py-2 text-slate-700 text-[13px] italic flex items-start gap-2" data-testid="quote-submitter-note">
                    <span className="not-italic stamp-label text-amber-700 shrink-0 pt-px">Submitter's note</span>
                    <span>“{pendingSubmitEv.note}”</span>
                  </div>
                )}
              </div>
              {quote.status !== "accepted" && (
                <Button size="sm" variant="outline" onClick={() => reviseQuote.mutate()} disabled={reviseQuote.isPending}
                  className="rounded-sm border-amber-500 text-amber-700 hover:bg-amber-100 shrink-0" data-testid="revise-quote-banner-btn">
                  {reviseQuote.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Undo2 size={13} className="mr-1.5" />}
                  Revise quote
                </Button>
              )}
            </div>
          )}
          {quoteId && (approval === "pending_approval" || (quote.approval_thread || []).length > 0) && (
            <ApprovalThread kind="quote" itemId={quoteId} thread={quote.approval_thread || []}
              canReply={approval === "pending_approval" && (isApprover || user?.email === (quote.submitted_for_approval_by || pendingSubmitEv?.by))}
              onPosted={(msg) => { applyingRef.current = true; setQuote((q) => ({ ...q, approval_thread: [...(q.approval_thread || []), msg] })); }} />
          )}
          {quoteId && approval === "changes_requested" && (
            <div data-testid="quote-changes-banner" className="border-l-4 border-rose-500 bg-rose-50 rounded-sm p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-sm text-rose-900 flex-1 min-w-[220px]">
                  <b>Changes requested{quote.changes_requested_by_name ? ` by ${quote.changes_requested_by_name}` : ""}</b>
                  {quote.changes_requested_at ? ` · ${fmtDateTime(quote.changes_requested_at)}` : ""}
                  {quote.changes_requested_comment && (
                    <div className="mt-1.5 bg-white border border-rose-200 rounded-sm px-3 py-2 text-slate-700 text-[13px] italic" data-testid="quote-changes-comment">
                      “{quote.changes_requested_comment}”
                    </div>
                  )}
                </div>
                <Button size="sm" data-testid="resubmit-approval-banner-btn" disabled={submitApproval.isPending}
                  onClick={() => setResubmitOpen(true)}
                  className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white shrink-0">
                  <ShieldCheck size={13} className="mr-1.5" /> Resubmit for approval
                </Button>
              </div>
              <div className="text-[11px] text-rose-700">Editing is unlocked — make the changes and resubmit. The revision number is not changed by this.</div>
            </div>
          )}
          {quoteId && (quote.approval_trail || []).length > 0 && (
            <details className="border border-slate-200 bg-white rounded-sm px-3 py-2" data-testid="quote-approval-trail">
              <summary className="stamp-label text-slate-500 cursor-pointer select-none">Approval trail ({quote.approval_trail.length}) — internal only</summary>
              <div className="space-y-1 mt-2 max-h-36 overflow-y-auto text-[11px]">
                {[...quote.approval_trail].reverse().map((t, i) => (
                  <div key={i} className="flex gap-2 items-baseline">
                    <span className="font-mono text-slate-400 shrink-0 w-32">{fmtDateTime(t.at)}</span>
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 translate-y-px ${
                      { submitted: "bg-amber-400", resubmitted: "bg-amber-400", changes_requested: "bg-rose-500", approved: "bg-emerald-500", revised: "bg-slate-400" }[t.event] || "bg-slate-300"}`} />
                    <span className="text-slate-700">
                      <b>{{ submitted: "Submitted for approval", resubmitted: "Resubmitted", changes_requested: "Changes requested", approved: "Approved", revised: "Revised" }[t.event] || t.event}</b>
                      {" "}by {t.by_name || t.by}{t.note ? <span className="text-slate-500"> — “{t.note}”</span> : null}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
          <div className={locked ? "pointer-events-none opacity-60 select-none space-y-4 sm:space-y-6" : "space-y-4 sm:space-y-6"} data-testid="quote-editor-body" inert={locked || undefined}>
          {/* Customer card */}
          <Card className="rounded-sm border-slate-200 industrial-shadow p-4 sm:p-5">
            <div className="stamp-label text-cyan-600 mb-3">Customer</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <FormField label="Related deal / job (optional)">
                  <Select value={quote.deal_id || ""} onValueChange={(v) => applyDeal(v)}>
                    <SelectTrigger className="h-10 rounded-sm" data-testid="v2-deal-select">
                      <SelectValue placeholder="Optional — pick a deal to pull in client details" />
                    </SelectTrigger>
                    <SelectContent>
                      {allDeals.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.job_number ? `${d.job_number} — ` : ""}{d.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                {quote.deal_id ? (
                  <p className="text-[10px] text-slate-400 mt-1">Client details below were pulled from the deal — adjust if needed.</p>
                ) : (
                  <p className="text-[10px] text-slate-400 mt-1" data-testid="v2-deal-optional-hint">Linking a deal is optional — you can fill in client details directly below.</p>
                )}
              </div>
              {companyContacts.length > 1 && (
                <div className="sm:col-span-2">
                  <FormField label="Contact for this quote">
                    <Select value={quote.contact_id || ""} onValueChange={(v) => {
                      const pc = companyContacts.find((c) => c.id === v);
                      setQuote({
                        ...quote, contact_id: v,
                        customer_name: pc ? `${pc.first_name || ""} ${pc.last_name || ""}`.trim() : quote.customer_name,
                        customer_email: pc?.email || quote.customer_email,
                        customer_phone: pc?.phone || pc?.mobile_phone || quote.customer_phone,
                      });
                    }}>
                      <SelectTrigger className="h-10 rounded-sm" data-testid="v2-contact-select"><SelectValue placeholder="Choose contact…" /></SelectTrigger>
                      <SelectContent>
                        {companyContacts.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {`${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}{c.is_primary ? " (primary)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>
              )}
              <FormField label="Customer name" required>
                <Input data-testid="v2-customer-name" value={quote.customer_name}
                  onChange={(e) => setQuote({ ...quote, customer_name: e.target.value })}
                  placeholder="e.g. Bob Smith" className="h-10 rounded-sm" />
              </FormField>
              <FormField label="Site address">
                <AddressAutocomplete testid="v2-site-address" value={quote.site_address}
                  onChange={(v) => setQuote({ ...quote, site_address: v })}
                  onPlace={(p) => setQuote({ ...quote, site_address: p.formatted })}
                  placeholder="123 Main St, Gosford" className="h-10 rounded-sm" />
              </FormField>
              <FormField label="Email">
                <Input type="email" value={quote.customer_email}
                  onChange={(e) => setQuote({ ...quote, customer_email: e.target.value })}
                  className="h-10 rounded-sm" />
              </FormField>
              <FormField label="Phone">
                <Input value={quote.customer_phone}
                  onChange={(e) => setQuote({ ...quote, customer_phone: e.target.value })}
                  className="h-10 rounded-sm" />
              </FormField>
              <FormField label="Link to company">
                <Select value={quote.company_id || "none"} onValueChange={(v) => setQuote({ ...quote, company_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-10 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
          </Card>

          {/* Iter-66: Line Items — customer-facing scope blocks.
              Each Line = one navy tile on the customer PDF, with a title,
              an AI narrative, a single price, and (internal to the estimator)
              a list of nested sub-sections (fence + gate + labour + …). */}
          <Card className="rounded-sm border-slate-200 industrial-shadow p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="stamp-label text-cyan-600">Line Items</div>
                <p className="text-xs text-slate-500 mt-0.5">Each line is one customer-facing scope on the quote. Sub-sections below build the internal cost.</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-sm text-slate-600">
                  {(quote.lines || []).length} line{(quote.lines || []).length === 1 ? "" : "s"} · {quote.sections.length} section{quote.sections.length === 1 ? "" : "s"}
                </Badge>
                <Button
                  data-testid="add-line-item-btn"
                  onClick={addLine}
                  className="rounded-sm h-9 bg-[#0f1a2b] hover:bg-[#12233b] text-white"
                >
                  <Plus size={15} className="mr-1.5" /> Add line item
                </Button>
              </div>
            </div>

            {isLegacy && (
              <div className="mb-4 p-3 border border-amber-300 bg-amber-50 rounded-sm text-xs text-amber-900 flex items-start gap-2">
                <Sparkles size={14} className="shrink-0 mt-0.5 text-amber-700" />
                <div>
                  This quote uses the legacy line-item builder. Editing here will preserve the existing items and totals. To convert it to a section-based quote, add sections and clear line items.
                </div>
              </div>
            )}

            <div className="space-y-4">
              {(quote.lines || []).map((line, lineIdx) => (
                <LineItemCard
                  key={line.id}
                  line={line}
                  lineIdx={lineIdx}
                  materials={materials}
                  gpMarginPct={quote.gp_margin_percent ?? DEFAULT_GP}
                  aiBusy={aiLineBusy === lineIdx}
                  onUpdateLine={(patch) => updateLine(lineIdx, patch)}
                  onDuplicateLine={() => duplicateLine(lineIdx)}
                  onRemoveLine={() => removeLine(lineIdx)}
                  onGenerateAi={() => draftLineScope(lineIdx)}
                  isAiStale={
                    !!line.ai_meta && !!line.ai_meta.source_fp &&
                    line.ai_meta.source_fp !== _sectionsFingerprint(line.sections || [])
                  }
                  onAddSection={(type) => setAddOpen({ lineIdx, type })}
                  onOpenFab={(editIndex = null) => openFab(lineIdx, editIndex)}
                  onUpdateSection={(sectionIdx, patch) => updateSectionInLine(lineIdx, sectionIdx, patch)}
                  onDuplicateSection={(sectionIdx) => duplicateSectionInLine(lineIdx, sectionIdx)}
                  onRemoveSection={(sectionIdx) => removeSectionFromLine(lineIdx, sectionIdx)}
                  onOpenMaterial={setEditingMaterialId}
                />
              ))}
            </div>
          </Card>

          {/* Internal notes (customer-facing scope is per-line above). */}
          <Card className="rounded-sm border-slate-200 industrial-shadow p-4 sm:p-5 space-y-4">
            <div>
              <div className="stamp-label text-slate-500 mb-2">Internal notes (not shown to customer)</div>
              <Textarea rows={2} value={quote.notes || ""}
                onChange={(e) => setQuote({ ...quote, notes: e.target.value })}
                className="rounded-sm text-sm" data-testid="internal-notes" />
            </div>
            {isLegacy && (
              <div>
                <div className="stamp-label text-cyan-600 mb-2">Scope of works (legacy V1)</div>
                <Textarea rows={4} value={quote.scope_summary || ""}
                  onChange={(e) => setQuote({ ...quote, scope_summary: e.target.value })}
                  className="rounded-sm text-sm" data-testid="v2-scope" />
              </div>
            )}
          </Card>

          {/* AI Site Plan Reader — hidden per Trent, 31 Oct 2026. Re-enable by restoring <SitePlanReader quoteId={quoteId} /> here. */}

          {/* Site photos + AI before/after visuals */}
          {quoteId ? (
            <QuoteVisuals quoteId={quoteId} />
          ) : (
            <Card className="rounded-sm border-2 border-dashed border-slate-200 p-5 text-center text-sm text-slate-400" data-testid="visuals-locked">
              Site photos &amp; AI before/after previews unlock once the draft saves.
            </Card>
          )}

          {/* Brochures & design attachments */}
          {quoteId ? (
            <QuoteAttachments quoteId={quoteId} />
          ) : (
            <Card className="rounded-sm border-2 border-dashed border-slate-200 p-5 text-center text-sm text-slate-400" data-testid="attachments-locked">
              Brochures &amp; design attachments unlock once the draft saves — add a customer name and a section.
            </Card>
          )}
          </div>
        </div>

        {/* Sticky totals sidebar */}
        <aside className="lg:sticky lg:top-4 self-start space-y-3">
          <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden">
            <div className="bg-slate-900 text-white p-4 sm:p-5">
              <div className="stamp-label text-cyan-400 mb-1">Quote Total</div>
              <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="v2-total-sale">{fmtCurrency(totals.sale)}</div>
              <div className="text-[11px] text-slate-400 mt-1 font-mono">AUD ex GST</div>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              <Row label="Total cost" value={fmtCurrencyExact(totals.cost)} testid="v2-total-cost" />
              <Row label={`GP margin (${totals.gp.toFixed(1)}%)`} value={fmtCurrencyExact(totals.gpDollars)} accent testid="v2-gp-dollars" />
              <div className="h-px bg-slate-200 my-1" />
              <Row label="Sale price" value={fmtCurrencyExact(totals.sale)} bold />
            </div>

            {/* GP% control */}
            <div className={`border-t border-slate-200 p-4 sm:p-5 bg-slate-50 ${locked ? "pointer-events-none opacity-60 select-none" : ""}`} inert={locked || undefined}>
              <div className="flex items-center justify-between mb-2">
                <Label className="stamp-label text-slate-600">GP Margin %</Label>
                <div className="relative">
                  <Input
                    data-testid="v2-gp-input"
                    type="number" step="0.5" min="0" max="99"
                    value={quote.gp_margin_percent}
                    onChange={(e) => setQuote({ ...quote, gp_margin_percent: parseFloat(e.target.value) || 0 })}
                    className="w-24 h-10 rounded-sm text-right font-mono font-bold text-lg bg-white border-cyan-300 focus-visible:ring-cyan-500"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none">%</span>
                </div>
              </div>
              <Slider
                data-testid="v2-gp-slider"
                min={0} max={70} step={0.5}
                value={[quote.gp_margin_percent]}
                onValueChange={(v) => setQuote({ ...quote, gp_margin_percent: v[0] })}
                className="mt-1"
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1 stamp-label">
                <span>0%</span><span className="text-cyan-600">40% (default)</span><span>70%</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-2 leading-snug font-mono">Sale = Cost ÷ (1 − GP%)</p>
            </div>
          </Card>

          <div className="text-[11px] text-slate-400 px-2 leading-relaxed">
            <div className="font-semibold text-slate-500 mb-1">Sections</div>
            {(quote.sections || []).map((s) => (
              <div key={s.id} className="flex justify-between py-0.5">
                <span className="truncate mr-2">{s.label}</span>
                <span className="font-mono text-slate-500 shrink-0">
                  {fmtCurrencyExact(sectionCost(s))}
                </span>
              </div>
            ))}
          </div>

          {/* Live visual preview panel — desktop sticky sidebar */}
          <div className="hidden lg:block">
            <LivePreviewPanel
              quote={quote}
              totals={totals}
              sectionCost={sectionCost}
              onDownload={approval === "approved" ? downloadPreview : null}
              onOpenFull={openPreview}
              downloading={downloadingPreview}
              opening={previewLoading}
              quoteId={quoteId}
              onSectionClick={scrollToSection}
              shareResetKey={shareResetKey}
            />
          </div>
        </aside>
      </div>

      {/* Mobile: floating "Preview" button opens a bottom-sheet with the same live preview */}
      <MobilePreviewFab
        quote={quote}
        totals={totals}
        sectionCost={sectionCost}
        onDownload={approval === "approved" ? downloadPreview : null}
        onOpenFull={openPreview}
        downloading={downloadingPreview}
        opening={previewLoading}
        quoteId={quoteId}
        onSectionClick={scrollToSection}
        shareResetKey={shareResetKey}
      />

      {/* Add section dialog */}
      {addOpen && (
        <AddSectionDialog
          type={addOpen.type}
          materials={materials}
          onClose={() => setAddOpen(null)}
          onPick={(preset) => addSectionToLine(addOpen.lineIdx, preset)}
          nextLetter={nextLetter()}
        />
      )}

      {/* Live-edit a material master item from a section link. Once saved,
          we sync the new effective cost into every section that references
          this material_id in the CURRENT quote — so live changes are visible
          without having to remove & re-add the section. Historical (saved)
          quotes stay untouched because their rates are already snapshotted
          server-side. */}
      <MaterialEditDialog
        materialId={editingMaterialId}
        open={!!editingMaterialId}
        onClose={() => setEditingMaterialId(null)}
        onSaved={async (updated) => {
          if (!updated?.id) return;
          try {
            // Ask the server for the effective cost (override → BOM → fallback)
            const { data } = await api.get(`/materials/${updated.id}/cost`);
            const newRate = Number(data?.cost ?? updated.unit_price ?? 0);
            // Iter-66: rewrite the rate inside every nested section whose
            // material_id matches, then re-mirror flat sections[].
            setQuote((q) => {
              const nextLines = (q.lines || []).map((l) => ({
                ...l,
                sections: (l.sections || []).map((s) =>
                  s.material_id === updated.id ? { ...s, rate: newRate } : s,
                ),
              }));
              return { ...q, lines: nextLines, sections: flatSections(nextLines) };
            });
            // Iter-69 fix: also refresh the materials cache so future picks
            // (e.g. adding a section to line 2) use the just-saved effective_cost.
            qc.invalidateQueries({ queryKey: ["materials-all-q2"] });
            qc.invalidateQueries({ queryKey: ["materials"] });
            toast.success(`Section rate refreshed to ${newRate.toFixed(2)}/${updated.unit || "unit"}`);
          } catch (e) {
            // Non-fatal — the invalidated picker query will still refresh.
            console.warn("Could not refresh section rate:", e);
          }
        }}
      />

      {/* Fabrication calculator dialog */}
      <Dialog open={fabDialog.open} onOpenChange={(o) => { if (!o) { setFabDialog({ open: false, lineIdx: null, editIndex: null }); setFabDraft(null); } }}>
        <DialogContent className="rounded-sm sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="fab-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{fabDialog.editIndex != null ? "Edit fabrication" : "Add fabrication"}</DialogTitle>
          </DialogHeader>
          {fabDialog.open && (
            <FabricationCalculator
              initial={fabDialog.editIndex != null ? (quote.lines?.[fabDialog.lineIdx]?.sections || [])[fabDialog.editIndex]?.fabrication : undefined}
              onChange={(input, result) => setFabDraft({ input, result })}
            />
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setFabDialog({ open: false, lineIdx: null, editIndex: null }); setFabDraft(null); }} className="rounded-sm">Cancel</Button>
            <Button onClick={commitFab} data-testid="fab-commit-btn" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
              {fabDialog.editIndex != null ? "Update section" : "Add to quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {dealPeekOpen && quote.deal_id && (
        <DealPeek dealId={quote.deal_id} onClose={() => setDealPeekOpen(false)} />
      )}
    </div>
  );
}

// ---------- helpers ----------

function sectionCost(s) {
  const rate = parseFloat(s.rate) || 0;
  const unit = (s.unit || "LM").toUpperCase();
  const qty = parseFloat(s.qty) || 1;
  if (unit === "LM") return rate * (parseFloat(s.length_m) || 0) * Math.max(qty, 1);
  if (s.section_type === "daily") {
    const units = parseFloat(s.units) || 1;
    return rate * Math.max(units, 1) * Math.max(qty, 0);
  }
  return rate * qty;
}

// Iter-66: map a flat-section index back into (lineIdx, sectionIdx). The
// SitePlanReader still returns a flat section list, and a couple of legacy
// shims (`updateSection` / `removeSection` / `duplicateSection`) need the
// mapping to touch the right nested list.
function _flatIndexToLine(lines, flatIdx) {
  let seen = 0;
  const ls = lines || [];
  for (let i = 0; i < ls.length; i++) {
    const secs = ls[i].sections || [];
    if (flatIdx < seen + secs.length) {
      return { lineIdx: i, sectionIdx: flatIdx - seen };
    }
    seen += secs.length;
  }
  return { lineIdx: null, sectionIdx: null };
}

function FormField({ label, required, children }) {
  return (
    <div>
      <Label className="stamp-label text-slate-600">{label}{required && <span className="text-rose-500 ml-1">*</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Row({ label, value, accent, bold, testid }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600 text-xs uppercase tracking-wider">{label}</span>
      <span data-testid={testid} className={`font-mono ${bold ? "font-bold text-lg text-slate-900" : accent ? "font-semibold text-cyan-600" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}

// ---------- Line-item card (Iter-66) ----------
// One customer-facing scope block. Renders:
//   • Navy #NN tile
//   • Title input + narrative textarea + "Generate AI scope" button
//   • Toggle: show sub-section breakdown to customer (default off)
//   • Inline "Add fence / gate / labour / daily / fabrication / extra" controls
//   • Nested list of SectionCards
//   • Duplicate / delete line
function LineItemCard({
  line, lineIdx, materials, aiBusy, isAiStale, gpMarginPct,
  onUpdateLine, onDuplicateLine, onRemoveLine, onGenerateAi,
  onAddSection, onOpenFab,
  onUpdateSection, onDuplicateSection, onRemoveSection, onOpenMaterial,
}) {
  const secs = line.sections || [];
  // Iter-67 bug fix: show the SALE price (cost + GP margin) per line so
  // the estimator sees exactly what the customer will pay ex-GST — and
  // so the sum of line prices equals the quote's ex-GST sale total.
  const lineCost = secs.reduce((sum, s) => sum + sectionCost(s), 0);
  const gp = Math.min(Math.max(parseFloat(gpMarginPct) || 0, 0), 99);
  const lineSale = gp < 100 ? lineCost / (1 - gp / 100) : lineCost;

  return (
    <div
      className="border border-slate-200 rounded-sm bg-white overflow-hidden"
      data-testid={`line-item-${lineIdx}`}
    >
      {/* Header: navy tile + title + total + actions */}
      <div className="flex items-stretch border-b border-slate-100 bg-slate-50/50">
        <div
          className="w-11 flex items-center justify-center text-white font-bold text-lg"
          style={{ background: "#0f1a2b" }}
          aria-label={`Line ${lineIdx + 1}`}
        >
          {lineIdx + 1}
        </div>
        <div className="flex-1 flex items-center gap-2 px-3 py-2 min-w-0">
          <Input
            value={line.title}
            onChange={(e) => onUpdateLine({ title: e.target.value })}
            placeholder={`Line ${lineIdx + 1} title (e.g. Front boundary Colorbond)`}
            className="h-8 text-sm font-semibold border-0 shadow-none bg-transparent px-1 focus-visible:ring-1 focus-visible:ring-cyan-400"
            data-testid={`line-item-${lineIdx}-title`}
          />
          <Badge variant="outline" className="rounded-sm text-[10px] shrink-0 uppercase tracking-wider">
            {secs.length} sub-section{secs.length === 1 ? "" : "s"}
          </Badge>
        </div>
        <div className="flex items-center gap-1 pr-2">
          <div className="text-right mr-2">
            <div className="font-mono text-sm font-semibold text-slate-900" data-testid={`line-item-${lineIdx}-sale`}>
              {fmtCurrencyExact(lineSale)}
            </div>
            <div className="text-[9px] text-slate-400 uppercase tracking-wider" title={`Cost ${fmtCurrencyExact(lineCost)} + ${gp}% GP · ex-GST`}>
              sale · ex-gst
            </div>
          </div>
          <Button
            size="sm" variant="ghost" onClick={onDuplicateLine}
            className="h-7 w-7 p-0 text-slate-500 hover:text-cyan-600" title="Duplicate line"
            data-testid={`line-item-${lineIdx}-duplicate`}
          >
            <Copy size={13} />
          </Button>
          <Button
            size="sm" variant="ghost" onClick={onRemoveLine}
            className="h-7 w-7 p-0 text-slate-500 hover:text-rose-600" title="Remove line"
            data-testid={`line-item-${lineIdx}-remove`}
          >
            <Trash2 size={13} />
          </Button>
        </div>
      </div>

      {/* Body: AI narrative + show-breakdown toggle + Add-section controls + child cards */}
      <div className="p-3 sm:p-4 space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="stamp-label text-slate-600">Customer scope narrative</Label>
            <div className="flex items-center gap-2">
              {isAiStale && (
                <span
                  className="text-[10px] text-amber-600 uppercase tracking-wider"
                  data-testid={`line-item-${lineIdx}-ai-stale`}
                >
                  · sections changed — regenerate
                </span>
              )}
              <Button
                size="sm" variant="outline" onClick={onGenerateAi}
                disabled={aiBusy || secs.length === 0}
                className="rounded-sm h-8 text-xs"
                data-testid={`line-item-${lineIdx}-ai-btn`}
              >
                {aiBusy
                  ? <Loader2 size={13} className="animate-spin mr-1" />
                  : <Sparkles size={13} className="mr-1 text-cyan-600" />}
                {line.scope_narrative ? "Regenerate AI scope" : "Generate AI scope"}
              </Button>
            </div>
          </div>
          <Textarea
            rows={3}
            value={line.scope_narrative || ""}
            onChange={(e) => onUpdateLine({ scope_narrative: e.target.value })}
            placeholder="2–3 sentence customer-facing description of this line item. Click Generate AI scope to auto-draft from the sub-sections below."
            className="rounded-sm text-sm"
            data-testid={`line-item-${lineIdx}-narrative`}
          />
        </div>

        {/* Aug 2026 (Trent): "Show sub-section breakdown to customer" toggle removed —
            customers only ever see the AI narrative + one line price. */}

        {/* Nested Add controls */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 pt-1">
          <Button
            data-testid={`line-item-${lineIdx}-add-fence`}
            variant="outline" size="sm"
            onClick={() => onAddSection("fence")}
            className="rounded-sm h-9 border-cyan-300 text-cyan-700 hover:bg-cyan-50 hover:border-cyan-500"
          >
            <Ruler size={13} className="mr-1.5" /> Fence
          </Button>
          <Button
            data-testid={`line-item-${lineIdx}-add-gate`}
            variant="outline" size="sm"
            onClick={() => onAddSection("gate")}
            className="rounded-sm h-9 border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-500"
          >
            <DoorOpen size={13} className="mr-1.5" /> Gate
          </Button>
          <Button
            data-testid={`line-item-${lineIdx}-add-labour`}
            variant="outline" size="sm"
            onClick={() => onAddSection("labour")}
            className="rounded-sm h-9 border-violet-300 text-violet-700 hover:bg-violet-50 hover:border-violet-500"
          >
            <HardHat size={13} className="mr-1.5" /> Labour
          </Button>
          <Button
            data-testid={`line-item-${lineIdx}-add-daily`}
            variant="outline" size="sm"
            onClick={() => onAddSection("daily")}
            className="rounded-sm h-9 border-rose-300 text-rose-700 hover:bg-rose-50 hover:border-rose-500"
          >
            <CalendarClock size={13} className="mr-1.5" /> Daily
          </Button>
          <Button
            data-testid={`line-item-${lineIdx}-add-fabrication`}
            variant="outline" size="sm"
            onClick={() => onOpenFab(null)}
            className="rounded-sm h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-500"
          >
            <Hammer size={13} className="mr-1.5" /> Fabrication
          </Button>
          <Button
            data-testid={`line-item-${lineIdx}-add-extra`}
            variant="outline" size="sm"
            onClick={() => onAddSection("extra")}
            className="rounded-sm h-9 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-500"
          >
            <PackageIcon size={13} className="mr-1.5" /> Extra
          </Button>
        </div>

        {secs.length === 0 && (
          <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-sm mt-2">
            <p className="text-xs text-slate-400">
              No sub-sections yet — add fence, gate, labour, daily, fabrication or extra above to build this line item.
            </p>
          </div>
        )}

        {/* Nested section cards */}
        {secs.length > 0 && (
          <div className="space-y-3 pt-1">
            {secs.map((s, sIdx) => (
              <div key={s.id} id={`section-${s.id}`} className="scroll-mt-24">
                <SectionCard
                  section={s}
                  index={sIdx}
                  lineIdx={lineIdx}
                  materials={materials}
                  onUpdate={(patch) => onUpdateSection(sIdx, patch)}
                  onRemove={() => onRemoveSection(sIdx)}
                  onDuplicate={() => onDuplicateSection(sIdx)}
                  onEditFab={() => onOpenFab(sIdx)}
                  onOpenMaterial={s.material_id ? () => onOpenMaterial(s.material_id) : null}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Section card ----------
function SectionCard({ section, index, lineIdx, materials, onUpdate, onRemove, onDuplicate, onEditFab, onOpenMaterial }) {
  const s = section;
  const isFence = s.section_type === "fence";
  const isGate = s.section_type === "gate";
  const isLabour = s.section_type === "labour";
  const isFab = s.section_type === "fabrication";
  const isDaily = s.section_type === "daily";
  const showColour = isFence && HAS_COLOUR_PICKER.has(s.fence_type);
  const heights = HEIGHT_OPTIONS[s.fence_type] || HEIGHT_OPTIONS.other;
  // Iter-64: scope narrative + nested components panel — collapsible so the
  // card stays scannable when the estimator hasn't yet enriched the section.
  const hasScope = (s.scope_narrative && s.scope_narrative.trim().length > 0) ||
                   (s.components && s.components.length > 0);
  const [scopeOpen, setScopeOpen] = useState(hasScope);

  const tint = isFence ? "border-l-cyan-500" : isGate ? "border-l-amber-500" : isLabour ? "border-l-violet-500" : isFab ? "border-l-emerald-500" : isDaily ? "border-l-rose-500" : "border-l-slate-400";

  const cost = sectionCost(s);
  const calc = calcSectionMaterials(s);
  // Iter-66: testids are scoped per-line so multiple lines with the same
  // section index don't collide. Falls back to legacy `${tid}`
  // when the card is rendered outside a line context.
  const tid = lineIdx != null ? `line-${lineIdx}-section-${index}` : `section-${index}`;

  return (
    <div className={`border border-slate-200 border-l-4 ${tint} rounded-sm bg-white`} data-testid={tid}>
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-center gap-2 min-w-0">
          <GripVertical size={14} className="text-slate-300 shrink-0" />
          <Input
            value={s.label}
            onChange={(e) => onUpdate({ label: e.target.value })}
            className="h-7 text-sm font-semibold border-0 shadow-none bg-transparent px-1 focus-visible:ring-1 focus-visible:ring-cyan-400"
            data-testid={`${tid}-label`}
          />
          {onOpenMaterial && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onOpenMaterial}
              className="h-6 w-6 p-0 text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50 shrink-0"
              title="Open material master — edit cost / BOM live"
              data-testid={`${tid}-open-material`}
            >
              <Link2 size={12} />
            </Button>
          )}
          <Badge variant="outline" className="rounded-sm text-[10px] shrink-0 uppercase tracking-wider">
            {s.section_type}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <div className="text-right mr-2">
            <div className="font-mono text-sm font-semibold text-slate-900" data-testid={`${tid}-cost`}>{fmtCurrencyExact(cost)}</div>
            <div className="text-[9px] text-slate-400 uppercase tracking-wider">cost</div>
          </div>
          <Button size="sm" variant="ghost" onClick={onDuplicate} className="h-7 w-7 p-0 text-slate-500 hover:text-cyan-600" title="Duplicate">
            <Copy size={13} />
          </Button>
          <Button size="sm" variant="ghost" onClick={onRemove} data-testid={`${tid}-remove`} className="h-7 w-7 p-0 text-slate-500 hover:text-rose-600" title="Remove">
            <Trash2 size={13} />
          </Button>
        </div>
      </div>

      {isFab && (
        <div className="p-3 sm:p-4 space-y-3">
          <div className="text-sm text-slate-700 font-medium">{FAB_TYPE_LABEL[s.fabrication?.fab_type] || "Fabrication"}</div>
          <div className="text-xs text-slate-500">{fabInputSummary(s.fabrication)}</div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="text-[11px] text-slate-400">Cost auto-calculated from the fabrication spec &amp; BOM.</span>
            <Button size="sm" variant="outline" onClick={onEditFab} data-testid={`${tid}-edit-fab`}
              className="rounded-sm h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <Hammer size={13} className="mr-1.5" /> Edit &amp; blueprint
            </Button>
          </div>
        </div>
      )}

      {!isFab && (
      <div className="p-3 sm:p-4 grid sm:grid-cols-2 gap-3">
        {isFence && (
          <>
            <FormField label="Fence type">
              <Select value={s.fence_type} onValueChange={(v) => {
                const hs = HEIGHT_OPTIONS[v] || HEIGHT_OPTIONS.other;
                const nextHeight = hs.includes(s.height_mm) ? s.height_mm : hs[0];
                onUpdate({ fence_type: v, height_mm: nextHeight, colour: null, colour_hex: null });
              }}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid={`${tid}-fence-type`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FENCE_TYPE_OPTIONS.map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Height (mm)">
              <Select value={String(s.height_mm)} onValueChange={(v) => onUpdate({ height_mm: parseInt(v, 10) })}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid={`${tid}-height`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {heights.map((h) => <SelectItem key={h} value={String(h)}>{h} mm ({(h / 1000).toFixed(1)}m)</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>

            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="stamp-label text-slate-600">Length</Label>
                <div className="flex items-center gap-1">
                  <Input
                    data-testid={`${tid}-length`}
                    type="number" step="0.1" min="0" value={s.length_m}
                    onChange={(e) => onUpdate({ length_m: parseFloat(e.target.value) || 0 })}
                    className="w-24 h-8 rounded-sm text-right font-mono text-sm"
                  />
                  <span className="text-[11px] text-slate-500">m</span>
                </div>
              </div>
              <Slider min={0} max={100} step={0.5}
                value={[Math.min(parseFloat(s.length_m) || 0, 100)]}
                onValueChange={(v) => onUpdate({ length_m: v[0] })} />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1 stamp-label"><span>0m</span><span>50m</span><span>100m</span></div>
            </div>

            {showColour && (
              <div className="sm:col-span-2">
                <Label className="stamp-label text-slate-600 mb-2 block">Colorbond® colour</Label>
                <div className="grid grid-cols-4 gap-2">
                  {COLORBOND_COLOURS.map((c) => {
                    const active = s.colour === c.name;
                    return (
                      <button
                        key={c.name}
                        type="button"
                        data-testid={`swatch-${c.name.replace(/\s/g, "-").toLowerCase()}`}
                        onClick={() => onUpdate({ colour: c.name, colour_hex: c.hex })}
                        className={`group relative flex flex-col items-center gap-1 p-2 rounded-sm border-2 transition-all ${active ? "border-cyan-600 shadow-sm" : "border-transparent hover:border-slate-300"}`}
                      >
                        <div
                          className="w-full h-10 rounded-sm border border-slate-200 shadow-inner"
                          style={{ backgroundColor: c.hex }}
                        />
                        <span className="text-[10px] text-slate-700 leading-tight text-center">{c.name}</span>
                        {active && (
                          <div className="absolute top-1 right-1 w-4 h-4 bg-cyan-600 rounded-full flex items-center justify-center">
                            <Check size={10} className="text-white" strokeWidth={3} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {isGate && (
          <>
            <FormField label="Gate template">
              <Select value={s.gate_template_id || ""} onValueChange={(v) => {
                const tpl = GATE_TEMPLATES.find((g) => g.id === v);
                onUpdate({ gate_template_id: v, length_m: tpl?.default_width_m || s.length_m, label: tpl ? `Gate — ${tpl.label}` : s.label });
              }}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid={`${tid}-gate-tpl`}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {GATE_TEMPLATES.map((g) => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Gate style / infill">
              <Select value={s.gate_style || "colorbond"} onValueChange={(v) => onUpdate({ gate_style: v, ...(v !== "colorbond" ? { colour: null, colour_hex: null } : {}) })}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid={`${tid}-gate-style`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GATE_STYLES.map((g) => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            {/* Custom size is ALWAYS editable. The template above just pre-fills
                the width — clients often need a bespoke opening. */}
            <FormField label="Custom size">
              <div className="flex gap-2">
                <Input type="number" step="0.1" min="0.1" value={s.length_m || ""} placeholder="Width (m)"
                  onChange={(e) => onUpdate({ length_m: parseFloat(e.target.value) || 0 })}
                  className="h-9 rounded-sm font-mono" data-testid={`${tid}-gate-width`} />
                <Input type="number" step="50" min="600" value={s.height_mm || 1800} placeholder="Height (mm)"
                  onChange={(e) => onUpdate({ height_mm: parseInt(e.target.value, 10) || 1800 })}
                  className="h-9 rounded-sm font-mono" data-testid={`${tid}-gate-height`} />
              </div>
              <div className="text-[10px] text-slate-400 mt-1">width m × height mm — override the template preset above.</div>
            </FormField>
            <FormField label="Build material">
              <Select value={s.build_material || "aluminium"} onValueChange={(v) => onUpdate({ build_material: v })}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid={`${tid}-gate-material`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aluminium">Aluminium build</SelectItem>
                  <SelectItem value="steel">Steel build</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Quantity">
              <Input type="number" min="1" value={s.qty} onChange={(e) => onUpdate({ qty: parseInt(e.target.value, 10) || 1 })} className="h-9 rounded-sm font-mono" data-testid={`${tid}-qty`} />
            </FormField>
            <FormField label="Gate posts (per gate)">
              <Select value={String(s.gate_posts ?? 2)} onValueChange={(v) => onUpdate({ gate_posts: parseInt(v, 10) })}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid={`${tid}-gate-posts`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0 — hang off existing posts</SelectItem>
                  <SelectItem value="1">1 post</SelectItem>
                  <SelectItem value="2">2 posts (standard)</SelectItem>
                  <SelectItem value="3">3 posts (double + centre)</SelectItem>
                  <SelectItem value="4">4 posts</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            {(s.gate_style || "colorbond") === "colorbond" && (
              <FormField label="Colorbond colour (optional)">
                <Select value={s.colour || "none"} onValueChange={(v) => {
                  if (v === "none") onUpdate({ colour: null, colour_hex: null });
                  else {
                    const c = COLORBOND_COLOURS.find((x) => x.name === v);
                    onUpdate({ colour: v, colour_hex: c?.hex || null });
                  }
                }}>
                  <SelectTrigger className="h-9 rounded-sm text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {COLORBOND_COLOURS.map((c) => (
                      <SelectItem key={c.name} value={c.name}>
                        <span className="inline-flex items-center gap-2">
                          <span className="w-3 h-3 rounded-sm border border-slate-200" style={{ backgroundColor: c.hex }} />
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            )}
          </>
        )}

        {s.section_type === "extra" && (
          <>
            <FormField label="Quantity">
              <Input type="number" step="0.1" min="0" value={s.qty} onChange={(e) => onUpdate({ qty: parseFloat(e.target.value) || 0 })} className="h-9 rounded-sm font-mono" data-testid={`${tid}-qty`} />
            </FormField>
            <FormField label="Unit">
              <Select value={s.unit} onValueChange={(v) => onUpdate({ unit: v })}>
                <SelectTrigger className="h-9 rounded-sm text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LM">per LM</SelectItem>
                  <SelectItem value="each">each</SelectItem>
                  <SelectItem value="m2">per m²</SelectItem>
                  <SelectItem value="hour">per hour</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </>
        )}

        {isDaily && (
          <>
            <FormField label="Kind">
              <Select value={s.daily_kind || "labour"} onValueChange={(v) => onUpdate({ daily_kind: v })}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid={`${tid}-daily-kind`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="labour">Day labour (person-days)</SelectItem>
                  <SelectItem value="equipment">Equipment hire</SelectItem>
                  <SelectItem value="custom">Custom day-rate item</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label={s.daily_kind === "equipment" ? "Units on-site" : "People on-site"}>
              <Input
                data-testid={`${tid}-units`}
                type="number" step="1" min="1" value={s.units ?? 1}
                onChange={(e) => onUpdate({ units: parseFloat(e.target.value) || 1, unit: "day" })}
                className="h-9 rounded-sm font-mono text-sm"
              />
            </FormField>
            <FormField label="Days">
              <Input
                data-testid={`${tid}-days`}
                type="number" step="0.5" min="0" value={s.qty}
                onChange={(e) => onUpdate({ qty: parseFloat(e.target.value) || 0, unit: "day" })}
                className="h-9 rounded-sm font-mono text-sm"
              />
            </FormField>
          </>
        )}

        {isLabour && (
          <FormField label="Man-days">
            <Input type="number" step="0.5" min="0" value={s.qty}
              onChange={(e) => onUpdate({ qty: parseFloat(e.target.value) || 0, unit: "day" })}
              className="h-9 rounded-sm font-mono" data-testid={`${tid}-qty`} />
          </FormField>
        )}

        <FormField label={isDaily ? "Rate per day (cost)" : isLabour ? "Rate per man-day (cost)" : "Rate (cost, editable)"}>
          <div className="relative">
            <DollarSign size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid={`${tid}-rate`}
              type="number" step="0.01" min="0" value={s.rate}
              onChange={(e) => onUpdate({ rate: parseFloat(e.target.value) || 0 })}
              className="h-9 pl-7 rounded-sm font-mono font-semibold text-sm"
            />
          </div>
        </FormField>
        <FormField label={`Section notes`}>
          <Input value={s.notes || ""} onChange={(e) => onUpdate({ notes: e.target.value })} className="h-9 rounded-sm text-sm" placeholder="e.g. remove existing timber first" />
        </FormField>

        {calc && (
          <div className="sm:col-span-2 flex flex-wrap items-center gap-x-4 gap-y-1 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid={`${tid}-autocalc`}>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Auto take-off</span>
            {calc.isGate ? (
              <span className="text-xs font-mono text-slate-700" data-testid={`${tid}-autocalc-posts`}>
                {calc.posts} gate post{calc.posts === 1 ? "" : "s"} <span className="text-slate-400">({calc.perGate}/gate × {calc.qty})</span>
              </span>
            ) : (
              <>
                <span className="text-xs font-mono text-slate-700" data-testid={`${tid}-autocalc-panels`}>{calc.panels} panel{calc.panels === 1 ? "" : "s"}</span>
                <span className="text-xs font-mono text-slate-700" data-testid={`${tid}-autocalc-posts`}>{calc.posts} posts</span>
              </>
            )}
            <span className="text-xs font-mono text-slate-700" data-testid={`${tid}-autocalc-concrete`}>
              {calc.concreteBags} bag{calc.concreteBags === 1 ? "" : "s"} concrete <span className="text-slate-400">(2/post)</span>
            </span>
          </div>
        )}
      </div>
      )}
      {/* Iter-64: Scope-of-works narrative + nested components. Collapsible
          so estimators see it opt-in. When populated, the customer PDF/web
          quote renders the scope narrative under this section, and — if
          `show_components_to_customer` is on — the itemised components. */}
      {!isFab && (
        <SectionScopePanel
          section={s}
          index={index}
          tid={tid}
          materials={materials || []}
          open={scopeOpen}
          onToggle={() => setScopeOpen((v) => !v)}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
}

// ---------- Section Scope Panel (iter-64) ----------
function SectionScopePanel({ section, index, tid, materials, open, onToggle, onUpdate }) {
  const s = section;
  // Iter-66: `tid` is passed by SectionCard as its scoped test-id prefix
  // (e.g. `line-0-section-1`). Retain a legacy fallback in case someone
  // renders this panel standalone.
  if (!tid) tid = `section-${index}`;
  const comps = useMemo(() => s.components || [], [s.components]);
  const setComps = (next) => onUpdate({ components: next });
  // A quick lookup of materials for the picker + live cost preview.
  const matById = useMemo(() => Object.fromEntries((materials || []).map((m) => [m.id, m])), [materials]);
  // Line cost preview — mirrors backend resolve_section_components math.
  const perUnitCost = useMemo(() => {
    let t = 0;
    for (const c of comps) {
      const m = matById[c.material_id];
      if (!m) continue;
      const qty = Number(c.qty_per_unit || 0);
      const raw = Number(m.effective_cost != null ? m.effective_cost : m.unit_price || 0);
      const mode = c.qty_mode === "pack" ? "pack" : "unit";
      const packSize = Number(m.pack_size || 0);
      const eff = mode === "pack" && packSize > 0 ? raw / packSize : raw;
      const waste = Number(m.waste_pct || 0);
      t += qty * eff * (1 + waste / 100);
    }
    return t;
  }, [comps, matById]);
  const hasComponents = comps.length > 0;
  const scopeSummary = (s.scope_narrative || "").trim();

  return (
    <div className="border-t border-slate-100" data-testid={`${tid}-scope`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-50/70 transition-colors"
        data-testid={`${tid}-scope-toggle`}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
          <span className="stamp-label text-[11px] text-slate-600">Scope of works &amp; materials</span>
          {scopeSummary && (
            <Badge variant="outline" className="rounded-sm text-[9px] py-0 h-4 px-1.5 border-cyan-200 bg-cyan-50 text-cyan-700">
              narrative
            </Badge>
          )}
          {hasComponents && (
            <Badge variant="outline" className="rounded-sm text-[9px] py-0 h-4 px-1.5 border-emerald-200 bg-emerald-50 text-emerald-700" data-testid={`${tid}-comp-count`}>
              {comps.length} component{comps.length === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
        {hasComponents && (
          <span className="text-[10px] text-slate-500 font-mono">
            derived rate ≈ ${perUnitCost.toFixed(2)}
          </span>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          {/* Scope narrative */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="stamp-label text-slate-600">Scope narrative (customer-facing)</Label>
              <span className="text-[9px] text-slate-400">shown on customer quote</span>
            </div>
            <Textarea
              data-testid={`${tid}-scope-narrative`}
              value={s.scope_narrative || ""}
              onChange={(e) => onUpdate({ scope_narrative: e.target.value })}
              rows={3}
              placeholder="Describe what's being done in this scope. e.g. We'll install a 24 m Colorbond Monument fence along the front boundary, including footings and site clean-up."
              className="rounded-sm text-sm"
            />
          </div>

          {/* Components table */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="stamp-label text-slate-600">Materials in this scope</Label>
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-slate-500 flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!s.show_components_to_customer}
                    onChange={(e) => onUpdate({ show_components_to_customer: e.target.checked })}
                    data-testid={`${tid}-show-comps`}
                    className="h-3 w-3 accent-cyan-600"
                  />
                  Show on customer quote
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-sm text-[11px]"
                  onClick={() => setComps([...comps, { id: cryptoRandom(), material_id: "", qty_per_unit: 1, qty_mode: "unit" }])}
                  data-testid={`${tid}-add-component`}
                >
                  <Plus size={12} className="mr-1" /> Add material
                </Button>
              </div>
            </div>
            {comps.length === 0 ? (
              <div className="text-[11px] text-slate-400 italic bg-slate-50 border border-dashed border-slate-200 rounded-sm px-3 py-2">
                No components yet. Add materials to derive this section's rate automatically from the Material Master.
              </div>
            ) : (
              <div className="space-y-1.5">
                {comps.map((c, ci) => {
                  const m = matById[c.material_id];
                  const raw = m ? Number(m.effective_cost != null ? m.effective_cost : m.unit_price || 0) : 0;
                  const packSize = m ? Number(m.pack_size || 0) : 0;
                  const mode = c.qty_mode === "pack" ? "pack" : "unit";
                  const eff = mode === "pack" && packSize > 0 ? raw / packSize : raw;
                  const waste = m ? Number(m.waste_pct || 0) : 0;
                  const line = Number(c.qty_per_unit || 0) * eff * (1 + waste / 100);
                  const canPack = m && packSize > 0;
                  return (
                    <div key={c.id || ci} className="grid grid-cols-12 gap-1.5 items-center bg-white border border-slate-200 rounded-sm px-2 py-1.5" data-testid={`${tid}-component-${ci}`}>
                      <div className="col-span-12 sm:col-span-5">
                        <Select value={c.material_id || undefined} onValueChange={(v) => {
                          const next = [...comps];
                          const picked = matById[v];
                          next[ci] = { ...next[ci], material_id: v, name: picked?.name || next[ci].name };
                          setComps(next);
                        }}>
                          <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`${tid}-component-${ci}-picker`}>
                            <SelectValue placeholder="Pick a material..." />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {materials.filter((mm) => mm && mm.id).map((mm) => (
                              <SelectItem key={mm.id} value={mm.id}>
                                <span className="text-xs">{mm.name}</span>
                                <span className="text-[10px] text-slate-400 ml-2">${Number(mm.effective_cost != null ? mm.effective_cost : mm.unit_price || 0).toFixed(2)}/{mm.unit}</span>
                                {Number(mm.pack_size) > 0 && (
                                  <span className="text-[10px] text-cyan-600 ml-1">· {Number(mm.pack_size)}{mm.pack_unit || "m"}/pack</span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Input
                          type="number"
                          step="0.01"
                          value={c.qty_per_unit}
                          onChange={(e) => {
                            const next = [...comps];
                            next[ci] = { ...next[ci], qty_per_unit: e.target.value === "" ? 0 : Number(e.target.value) };
                            setComps(next);
                          }}
                          className="h-8 rounded-sm font-mono text-xs text-right"
                          data-testid={`${tid}-component-${ci}-qty`}
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Select value={mode} onValueChange={(v) => {
                          const next = [...comps];
                          next[ci] = { ...next[ci], qty_mode: v };
                          setComps(next);
                        }}>
                          <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`${tid}-component-${ci}-mode`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unit"><span className="text-xs">per {m?.unit || "unit"}</span></SelectItem>
                            <SelectItem value="pack" disabled={!canPack}><span className="text-xs">per {m?.pack_unit || "m"} (pack)</span></SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3 sm:col-span-2 text-right font-mono text-xs text-slate-900" data-testid={`${tid}-component-${ci}-line-cost`}>
                        {m ? `$${line.toFixed(2)}` : <span className="text-[10px] text-slate-400">—</span>}
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"
                          onClick={() => setComps(comps.filter((_, i) => i !== ci))}
                          data-testid={`${tid}-component-${ci}-remove`}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center justify-end gap-3 text-[11px] pt-1">
                  <span className="text-slate-500 stamp-label">Derived rate / {s.units || (s.section_type === "gate" || s.section_type === "fabrication" ? "unit" : "m")}</span>
                  <span className="font-mono font-semibold text-cyan-700" data-testid={`${tid}-derived-rate`}>${perUnitCost.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// tiny id helper for temporary React keys — persisted rows get server ids on save
function cryptoRandom() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}

// ---------- Add Section Dialog ----------
function AddSectionDialog({ type, materials, onClose, onPick, nextLetter }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [creatingMat, setCreatingMat] = useState(false);
  const [newMat, setNewMat] = useState({ name: "", unit: type === "daily" ? "day" : "ea", unit_price: "" });
  const [savingMat, setSavingMat] = useState(false);
  const q = search.toLowerCase();

  // Unified picker — one source of truth (materials collection). Composites
  // (bundled sell-per-LM items migrated from the retired /pricing page) are
  // just materials with is_composite=true and float to the top for the
  // relevant section type.
  const matches = (m) => {
    const cat = m.category;
    const unit = (m.unit || "").toLowerCase();
    if (type === "fence") return cat === "material" && (m.is_composite || (m.fence_types || []).length > 0);
    if (type === "gate") return m.is_composite && (/gate/i.test(m.name || "") || (m.fence_types || []).includes("custom_gate") || (m.sku || "").toUpperCase().includes("COMP-GATE"));
    if (type === "extra") return cat === "incidental";
    if (type === "labour") return cat === "labour";
    if (type === "daily") return cat === "labour" || unit === "day";
    return false;
  };
  const mats = (materials || [])
    .filter((m) => !search || (m.name || "").toLowerCase().includes(q) || (m.sku || "").toLowerCase().includes(q))
    .filter(matches)
    .sort((a, b) => {
      // Composites first (they're the "ready-to-quote" bundles), then raw materials
      if (!!a.is_composite !== !!b.is_composite) return a.is_composite ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });

  const saveNewMaterial = async () => {
    if (!newMat.name.trim()) { toast.error("Material name is required"); return; }
    setSavingMat(true);
    try {
      const { data } = await api.post("/materials", {
        name: newMat.name.trim(),
        unit: newMat.unit || "ea",
        unit_price: parseFloat(newMat.unit_price) || 0,
        category: type === "labour" ? "labour" : type === "daily" ? (newMat.unit === "day" ? "labour" : "incidental") : type === "extra" ? "incidental" : "material",
        fence_types: type === "gate" ? ["custom_gate"] : [],
      });
      qc.invalidateQueries({ queryKey: ["materials-all-q2"] });
      qc.invalidateQueries({ queryKey: ["materials"] });
      toast.success(`${data.name} added to the Material Master (SKU ${data.sku})`);
      pickMaterial(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save material");
    } finally {
      setSavingMat(false);
    }
  };

  const title = type === "fence" ? "Add Fence Section"
    : type === "gate" ? "Add Gate"
    : type === "labour" ? "Add Labour"
    : type === "daily" ? "Add Daily Cost — Labour · Equipment · Custom"
    : "Add Extra";

  const pickMaterial = (m) => {
    const u = materialUnitToSection(m.unit);
    // Iter-69 fix: use effective_cost (BOM-derived / override / unit_price fallback)
    // so composite bundles priced per-LM via pack_size come in at the correct
    // rate on EVERY pick — not just when MaterialEditDialog.onSaved happens to
    // resync line 1. Previously `unit_price` was 0 for BOM-derived composites
    // (Bug: line 2 always came in at $0 while line 1 got retroactively fixed).
    const derivedCost = Number(m.effective_cost != null ? m.effective_cost : (m.unit_price || 0)) || 0;
    // Iter-69 fix (2nd pass): FENCE sections are always per-linear-metre in
    // Trent's model, no matter what unit the composite bundle is stored as
    // (some bundles are seeded as unit="ea" but their effective_cost is a
    // per-LM rate). Force unit=LM + default length=10m so the length field
    // multiplies through, matching the "Add custom (blank)" fence path.
    const sectionUnit = type === "fence" ? "LM"
      : (type === "labour" || type === "daily") ? "day"
      : u;
    const base = {
      id: crypto.randomUUID(),
      section_type: type,
      label: type === "fence" ? `Section ${nextLetter} — ${m.name}` : type === "gate" ? `Gate — ${m.name}` : m.name,
      rate: derivedCost,
      unit: sectionUnit,
      qty: 1,
      length_m: sectionUnit === "LM" ? 10 : 0,
      material_id: m.id,
      category: m.category,
      source: m.is_composite ? "composite" : "material",
      notes: m.needs_cost_review ? "⚠️ Rate needs cost review — see Material Master" : "",
    };
    // For fence composites, honour their fence_type + height defaults
    if (type === "fence") {
      const ft = (m.fence_types || [])[0] || "other";
      const heightMatch = (m.name || "").match(/(\d\.\d|\d{3,4})/);
      const height = heightMatch ? (heightMatch[1].includes(".") ? Math.round(parseFloat(heightMatch[1]) * 1000) : parseInt(heightMatch[1], 10)) : 1800;
      Object.assign(base, { fence_type: ft, height_mm: height, colour: null, colour_hex: null });
    }
    if (type === "gate") Object.assign(base, { gate_style: "colorbond", gate_template_id: null, gate_posts: 2, build_material: "aluminium", colour: null, colour_hex: null });
    if (type === "daily") Object.assign(base, { units: 1, daily_kind: m.category === "labour" ? "labour" : "equipment" });
    onPick(base);
  };

  const custom = () => {
    const base = {
      id: crypto.randomUUID(),
      section_type: type,
      label: type === "fence" ? `Section ${nextLetter} — Custom`
        : type === "gate" ? "Custom gate"
        : type === "labour" ? "Labour"
        : type === "daily" ? "Custom day-rate item"
        : "Custom extra",
      rate: 0,
      unit: type === "fence" ? "LM" : (type === "labour" || type === "daily") ? "day" : "each",
      qty: 1,
      source: "custom",
      notes: "",
    };
    if (type === "fence") Object.assign(base, { fence_type: "other", height_mm: 1800, length_m: 0 });
    if (type === "gate") Object.assign(base, { gate_template_id: null, gate_style: "colorbond", gate_posts: 2, build_material: "aluminium", length_m: 0 });
    if (type === "daily") Object.assign(base, { units: 1, daily_kind: "custom" });
    onPick(base);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="add-section-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2 mb-1">Pick from the Material Master. Rate is edited on the section after adding — GP margin applies once at the quote total.</p>
        <Input placeholder="Filter by name or SKU..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm h-9" data-testid="add-section-filter" />
        <div className="max-h-[340px] overflow-y-auto border border-slate-200 divide-y divide-slate-100 rounded-sm">
          {mats.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">
              No matching materials. Add one in <a href="/materials" className="text-cyan-600 underline">Material Master</a> or click <b>Add custom</b> below.
            </div>
          )}
          {mats.map((m) => (
            <button
              key={m.id}
              onClick={() => pickMaterial(m)}
              data-testid={`pick-material-${m.id}`}
              className="w-full text-left px-3 py-2 hover:bg-cyan-50 flex items-center justify-between gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <div className="text-sm font-medium text-slate-900 truncate">{m.name}</div>
                  {m.is_composite && <Badge variant="outline" className="rounded-sm text-[9px] border-cyan-300 bg-cyan-50 text-cyan-700 h-4 px-1">BUNDLE</Badge>}
                  {m.needs_cost_review && <Badge variant="outline" className="rounded-sm text-[9px] border-amber-300 bg-amber-50 text-amber-700 h-4 px-1">REVIEW</Badge>}
                </div>
                <div className="text-[10px] text-slate-500 truncate">
                  {(m.sku || m.category)} · per {m.unit}
                  {(m.fence_types || []).length ? ` · ${(m.fence_types || []).slice(0, 2).map((f) => FENCE_LABELS[f] || f).join(", ")}` : ""}
                </div>
              </div>
              <div className="font-mono text-sm font-semibold text-slate-900 shrink-0">
                {fmtCurrencyExact(m.effective_cost != null ? m.effective_cost : m.unit_price)}<span className="text-[10px] text-slate-500 ml-0.5">/{m.unit}</span>
              </div>
            </button>
          ))}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={custom} className="rounded-sm text-slate-600" data-testid="add-custom-rate-btn">
              <Plus size={14} className="mr-1" /> Add custom (blank)
            </Button>
            <Button variant="outline" onClick={() => setCreatingMat((v) => !v)} className="rounded-sm text-cyan-700 border-cyan-300" data-testid="new-material-btn">
              <PackageIcon size={14} className="mr-1" /> New material
            </Button>
          </div>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
        </DialogFooter>
        {creatingMat && (
          <div className="border border-cyan-200 bg-cyan-50/40 rounded-sm p-3 space-y-2" data-testid="new-material-form">
            <div className="stamp-label text-cyan-700 text-[10px]">New material — saves to the Material Master (SKU auto-generated)</div>
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Material name" value={newMat.name} onChange={(e) => setNewMat({ ...newMat, name: e.target.value })} className="rounded-sm h-9 col-span-3 sm:col-span-1" data-testid="new-material-name" />
              <Input placeholder="Unit (ea, lm, day…)" value={newMat.unit} onChange={(e) => setNewMat({ ...newMat, unit: e.target.value })} className="rounded-sm h-9" data-testid="new-material-unit" />
              <Input placeholder="Cost price $" type="number" step="0.01" value={newMat.unit_price} onChange={(e) => setNewMat({ ...newMat, unit_price: e.target.value })} className="rounded-sm h-9" data-testid="new-material-price" />
            </div>
            <Button size="sm" onClick={saveNewMaterial} disabled={savingMat} className="rounded-sm h-8 bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="save-new-material-btn">
              {savingMat ? <Loader2 size={13} className="animate-spin mr-1" /> : <Save size={13} className="mr-1" />} Save & add to quote
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
